import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, DatePicker, TimePicker, Space, message, Card, Tag, Popconfirm, Tooltip, Row, Col, Upload, Image, Select, Progress, Spin, Descriptions, Checkbox, Alert, InputNumber, Tabs, Divider } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, TeamOutlined, CopyOutlined, ExclamationCircleOutlined, DownloadOutlined, UploadOutlined, FileTextOutlined, NotificationOutlined, LockOutlined, UnlockOutlined, InboxOutlined, SearchOutlined, UserOutlined, GiftOutlined, SolutionOutlined, EyeOutlined, EyeInvisibleOutlined, MinusCircleOutlined, CarOutlined, SyncOutlined, RollbackOutlined, LinkOutlined } from '@ant-design/icons';
import VoucherWithdrawalModal from '../components/VoucherWithdrawalModal';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import dayjs from 'dayjs';
import 'moment/locale/zh-cn';
import 'dayjs/locale/zh-cn';
import authService from '../utils/auth';
import { reportExport } from '../utils/exportLog';
import { getCountryDisplay, getAllCountries } from '../utils/countryCode';
import { validatePassportName, getNameFormatHint } from '../utils/nameValidator';
import PassportReuseTag from '../components/PassportReuseTag';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import ChinaDocAction, { getChinaDocLabel } from '../components/ChinaDocAction';
import './TourManagement.css';

moment.locale('zh-cn');
dayjs.locale('zh-cn');

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const { Option } = Select;
const { Dragger } = Upload;

// 中国团附加证件的标签与查看/补传统一在 components/ChinaDocAction 里实现

const normalizePickupPoints = (points) => {
  if (!points) return [];
  return points.map(p => {
    if (typeof p === 'string') return { name: p, time: '' };
    return p;
  });
};

const normalizePickupPointsForForm = (points) => {
  if (!points) return [];
  return points.map(p => {
    if (typeof p === 'string') return { name: p, time: null };
    return { ...p, time: p.time ? dayjs(p.time, 'HH:mm') : null };
  });
};

// OCR 日期统一按 DD/MM/YYYY 优先解析成 YYYY-MM-DD（原生 date input 只认这个格式）。
// 解析不出来返回空串，让字段留空由人工补，绝不能塞 "Invalid date"——
// 那会被当成空值提交，MySQL 非严格模式下写成 0000-00-00，之后整条记录再也编辑不了（PUT 恒 400）。
const parseOcrDate = (v) => {
  if (!v) return '';
  const m = moment(v, ['DD/MM/YYYY', 'YYYY-MM-DD', 'YYYY/MM/DD', 'DD-MM-YYYY'], true);
  return m.isValid() ? m.format('YYYY-MM-DD') : '';
};

function TourManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // 从URL参数或sessionStorage恢复状态
  const getInitialPage = () => {
    const urlPage = searchParams.get('page');
    if (urlPage) return parseInt(urlPage, 10);
    const storedPage = sessionStorage.getItem('tourManagement_page');
    return storedPage ? parseInt(storedPage, 10) : 1;
  };

  const getInitialSearchProductName = () => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) return urlSearch;
    return sessionStorage.getItem('tourManagement_search') || '';
  };

  const getInitialSearchDate = () => {
    const urlDate = searchParams.get('date');
    if (urlDate) return dayjs(urlDate);
    const storedDate = sessionStorage.getItem('tourManagement_date');
    return storedDate ? dayjs(storedDate) : null;
  };

  const [tours, setTours] = useState([]);
  const [tourists, setTourists] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [touristModalVisible, setTouristModalVisible] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  const [editingTourist, setEditingTourist] = useState(null);
  const [ekokWarning, setEkokWarning] = useState('');
  const [editingRemarks, setEditingRemarks] = useState(null);
  const [remarksModalVisible, setRemarksModalVisible] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [newPassportPhoto, setNewPassportPhoto] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [searchProductName, setSearchProductName] = useState(getInitialSearchProductName);
  const [showExpired, setShowExpired] = useState(false);
  const [searchDepartureDate, setSearchDepartureDate] = useState(getInitialSearchDate);
  const [searchTouristName, setSearchTouristName] = useState('');
  const [allTourists, setAllTourists] = useState([]); // 存储所有游客数据用于搜索
  const [searchingTourist, setSearchingTourist] = useState(false);
  const [ekokMatchTourIds, setEkokMatchTourIds] = useState(null);
  // 正在导出的范围（passport / chinaDoc）。用于禁用按钮防连点：
  // 短时间内连续触发多次下载会被浏览器按「自动下载多个文件」拦截，且 JS 收不到任何回调。
  const [exportingScope, setExportingScope] = useState(null);
  const [ekokSearching, setEkokSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [pageSize, setPageSize] = useState(10);
  // 查看证件/护照弹窗
  const [passportModalVisible, setPassportModalVisible] = useState(false);
  const [passportModalRecord, setPassportModalRecord] = useState(null);
  // 机票上传相关状态
  const [ticketModalVisible, setTicketModalVisible] = useState(false);
  const [ticketUploadingTourist, setTicketUploadingTourist] = useState(null);
  const [ticketUploading, setTicketUploading] = useState(false);
  const [ticketOcrResult, setTicketOcrResult] = useState(null);
  const [ticketUploadProgress, setTicketUploadProgress] = useState(0);
  const [ticketProgressText, setTicketProgressText] = useState('');
  // Add-On设置相关状态
  const [addonModalVisible, setAddonModalVisible] = useState(false);
  const [addonSettingsTour, setAddonSettingsTour] = useState(null);
  const [addonData, setAddonData] = useState({ allAddOns: [], disabledAddonIds: [] });
  const [addonLoading, setAddonLoading] = useState(false);
  // 当前产品可用的Add-On列表（用于游客管理弹窗）
  const [availableAddOns, setAvailableAddOns] = useState([]);
  const [addTouristModalVisible, setAddTouristModalVisible] = useState(false);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundTourist, setRefundTourist] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [activeTab, setActiveTab] = useState(() => sessionStorage.getItem('tourMgmt_activeTab') || 'standard');
  // 截团相关
  const [lockModalVisible, setLockModalVisible] = useState(false);
  const [lockGuideId, setLockGuideId] = useState(undefined);
  const [lockLoading, setLockLoading] = useState(false);
  const [guides, setGuides] = useState([]);
  // 修改导游相关
  const [changeGuideVisible, setChangeGuideVisible] = useState(false);
  const [changeGuideId, setChangeGuideId] = useState(undefined);
  // 发送出团通知进度
  const [noticeSending, setNoticeSending] = useState(false);
  const [noticeProgress, setNoticeProgress] = useState(0);
  const [noticeProgressText, setNoticeProgressText] = useState('');
  // 自愿退团 / Voucher
  const [withdrawModalVisible, setWithdrawModalVisible] = useState(false);
  const [withdrawingTourist, setWithdrawingTourist] = useState(null);
  const [form] = Form.useForm();
  const [touristForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [remarksForm] = Form.useForm();

  useEffect(() => {
    // 只有在已登录时才获取数据
    if (authService.isAuthenticated()) {
      fetchTours();
      fetchDestinations();
      fetchGuides();
    }
  }, []);

  const fetchGuides = async () => {
    try {
      const response = await axios.get(`${API_BASE}/salespersons`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        setGuides(response.data.data.filter(s => s.role === 'guide'));
      }
    } catch (error) {
      console.error('获取导游列表失败:', error);
    }
  };

  // 保存页面状态到sessionStorage和URL参数
  useEffect(() => {
    // 保存到sessionStorage
    sessionStorage.setItem('tourManagement_page', currentPage.toString());
    if (searchProductName) {
      sessionStorage.setItem('tourManagement_search', searchProductName);
    } else {
      sessionStorage.removeItem('tourManagement_search');
    }
    if (searchDepartureDate && searchDepartureDate.isValid()) {
      sessionStorage.setItem('tourManagement_date', searchDepartureDate.format('YYYY-MM-DD'));
    } else {
      sessionStorage.removeItem('tourManagement_date');
    }

    // 更新URL参数（不导致重新导航）
    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (searchProductName) params.set('search', searchProductName);
    if (searchDepartureDate && searchDepartureDate.isValid()) {
      params.set('date', searchDepartureDate.format('YYYY-MM-DD'));
    }

    const newSearch = params.toString();
    const currentSearch = location.search.replace('?', '');
    if (newSearch !== currentSearch) {
      navigate(`${location.pathname}${newSearch ? '?' + newSearch : ''}`, { replace: true });
    }
  }, [currentPage, searchProductName, searchDepartureDate]);

  // 处理分页变化
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const fetchDestinations = async () => {
    try {
      const response = await axios.get(`${API_BASE}/destinations?isActive=true`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        setDestinations(response.data.data);
      }
    } catch (error) {
      console.error('获取目的地列表失败:', error);
    }
  };

    const fetchTours = async () => {
    // 再次检查认证状态
    if (!authService.isAuthenticated()) {
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/tours`, {
        headers: authService.getAuthHeaders()
      });
      setTours(response.data.data);
    } catch (error) {
      if (error.response?.status === 401) {
        // 401错误不显示消息，让路由保护处理
        return;
      }
      message.error('获取旅游产品列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchTourists = async (tourId) => {
    try {
      const response = await axios.get(`${API_BASE}/tourists/tour/${tourId}`, {
        headers: authService.getAuthHeaders()
      });
      setTourists(response.data.data);
    } catch (error) {
      message.error('获取游客列表失败');
    }
  };

  // 搜索游客姓名
  const searchTouristsByName = async (name) => {
    if (!name || name.trim() === '') {
      setAllTourists([]);
      return;
    }

    setSearchingTourist(true);
    try {
      const response = await axios.get(`${API_BASE}/tourists`, {
        headers: authService.getAuthHeaders()
      });

      if (response.data.success) {
        // 过滤匹配的游客
        const searchLower = name.toLowerCase().trim();
        const matchedTourists = response.data.data.filter(tourist =>
          tourist.touristName?.toLowerCase().includes(searchLower) ||
          tourist.passportName?.toLowerCase().includes(searchLower) ||
          tourist.passportNumber?.toLowerCase().includes(searchLower) ||
          tourist.ekok?.toLowerCase().includes(searchLower)
        );
        setAllTourists(matchedTourists);

        // 获取匹配游客所属的产品ID列表
        const matchedTourIds = [...new Set(matchedTourists.map(t => t.tourId))];
        return matchedTourIds;
      }
    } catch (error) {
      console.error('搜索游客失败:', error);
    } finally {
      setSearchingTourist(false);
    }
    return [];
  };

  const handleCreateTour = async (values) => {
    try {
      const payload = {
        departureDate: values.departureDate.format('YYYY-MM-DD'),
        destinationId: values.destinationId,
        tourType: values.tourType || 'standard'
      };
      if (values.tourType === 'daytour' && values.totalSeats != null) {
        payload.totalSeats = values.totalSeats;
      }
      if (values.tourType === 'daytour') {
        if (values.adultPrice != null) payload.adultPrice = values.adultPrice;
        if (values.seniorPrice != null) payload.seniorPrice = values.seniorPrice;
        if (values.adultCommission != null) payload.adultCommission = values.adultCommission;
        if (values.seniorCommission != null) payload.seniorCommission = values.seniorCommission;
      }
      const res = await axios.post(`${API_BASE}/tours`, payload, {
        headers: authService.getAuthHeaders()
      });
      message.success(`创建成功，团号: ${res.data.data.productName}`);
      setModalVisible(false);
      form.resetFields();
      fetchTours();
    } catch (error) {
      message.error(error.response?.data?.error || '创建失败');
    }
  };

  const handleUpdateTour = async (values) => {
    try {
      const isDaytourEdit = values.tourType === 'daytour';
      const canEditTourNumber = authService.isStaff();
      const payload = {
        productName: isDaytourEdit ? (canEditTourNumber && values.productName ? values.productName : selectedTour.productName) : values.productName,
        departureDate: values.departureDate.format('YYYY-MM-DD'),
        destinationId: values.destinationId || null,
        tourType: values.tourType || 'standard'
      };
      if (values.tourType === 'daytour' && values.totalSeats != null) {
        payload.totalSeats = values.totalSeats;
      } else {
        payload.totalSeats = null;
      }
      if (values.tourType === 'daytour') {
        payload.adultPrice = values.adultPrice ?? null;
        payload.seniorPrice = values.seniorPrice ?? null;
        payload.adultCommission = values.adultCommission ?? null;
        payload.seniorCommission = values.seniorCommission ?? null;
      } else {
        payload.adultPrice = null;
        payload.seniorPrice = null;
        payload.adultCommission = null;
        payload.seniorCommission = null;
      }
      await axios.put(`${API_BASE}/tours/${selectedTour.id}`, payload, {
        headers: authService.getAuthHeaders()
      });
      message.success('更新成功');
      setModalVisible(false);
      form.resetFields();
      setSelectedTour(null);
      fetchTours();
    } catch (error) {
      console.error('updateTour error:', error.response?.data || error);
      message.error(error.response?.data?.error || '更新失败');
    }
  };

  const handleDeleteTour = async (tourId) => {
    try {
      await axios.delete(`${API_BASE}/tours/${tourId}`, {
        headers: authService.getAuthHeaders()
      });
      message.success('删除成功');
      fetchTours();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleAddTourist = async (values) => {
    // 验证并格式化游客姓名
    const nameValidation = validatePassportName(values.touristName);
    if (!nameValidation.valid) {
      message.error(nameValidation.error);
      return;
    }
    
    try {
      const touristPayload = {
        tourId: selectedTour.id,
        touristName: nameValidation.formatted,
        salesName: values.salesName,
        ekok: values.ekok || null
      };
      if (selectedTour?.tourType === 'daytour') {
        if (values.phone) touristPayload.contactPhone = values.phone;
        if (values.discountPrice) touristPayload.discountPrice = true;
        if (values.pickupPoint) touristPayload.pickupPoint = values.pickupPoint;
      }
      const response = await axios.post(`${API_BASE}/tourists`, touristPayload, {
        headers: authService.getAuthHeaders()
      });
      
      const uploadUrl = response.data.uploadUrl;
      message.success('游客添加成功');

      // 一日游证件由会员系统收取，后台添加不再弹出上传链接
      if (selectedTour?.tourType === 'daytour') {
        touristForm.resetFields();
        setAddTouristModalVisible(false);
        fetchTourists(selectedTour.id);
        return;
      }

      Modal.info({
        title: '上传链接已生成',
        content: (
          <div>
            <p>请将以下链接发送给游客：</p>
            <Input.TextArea 
              value={uploadUrl} 
              readOnly 
              autoSize={{ minRows: 2, maxRows: 4 }}
            />
            <Button 
              type="primary" 
              onClick={async () => {
                // 降级方案函数
                const fallbackCopy = () => {
                  const textArea = document.createElement('textarea');
                  textArea.value = uploadUrl;
                  textArea.style.position = 'fixed';
                  textArea.style.left = '-9999px';
                  document.body.appendChild(textArea);
                  textArea.focus();
                  textArea.select();
                  try {
                    document.execCommand('copy');
                    message.success('链接已复制到剪贴板');
                  } catch (copyError) {
                    message.error('复制失败，请手动复制链接');
                  }
                  document.body.removeChild(textArea);
                };

                // 优先使用 Clipboard API
                if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
                  try {
                    await navigator.clipboard.writeText(uploadUrl);
                    message.success('链接已复制到剪贴板');
                  } catch (error) {
                    fallbackCopy();
                  }
                } else {
                  fallbackCopy();
                }
              }}
              style={{ marginTop: 10 }}
              icon={<CopyOutlined />}
            >
              复制链接
            </Button>
          </div>
        ),
        okText: '确定'
      });
      
      touristForm.resetFields();
      setAddTouristModalVisible(false);
      fetchTourists(selectedTour.id);
    } catch (error) {
      message.error('添加游客失败');
    }
  };

  const openTouristModal = async (tour) => {
    setSelectedTour(tour);
    fetchTourists(tour.id);
    setTouristModalVisible(true);

    // 一日游：获取目的地详情（含上车点）
    if (tour.tourType === 'daytour' && tour.destinationId) {
      try {
        const destResponse = await axios.get(`${API_BASE}/destinations/${tour.destinationId}`, {
          headers: authService.getAuthHeaders()
        });
        if (destResponse.data.success) {
          setSelectedTour(prev => ({ ...prev, destination: destResponse.data.data }));
        }
      } catch (error) {
        console.error('获取目的地详情失败:', error);
      }
    }

    // 获取该产品可用的Add-On列表
    if (tour.destinationId) {
      try {
        const response = await axios.get(`${API_BASE}/tours/${tour.id}/addons`, {
          headers: authService.getAuthHeaders()
        });
        if (response.data.success) {
          setAvailableAddOns(response.data.data.availableAddOns || []);
        }
      } catch (error) {
        console.error('获取Add-On列表失败:', error);
        setAvailableAddOns([]);
      }
    } else {
      setAvailableAddOns([]);
    }
  };

  const copyUploadLink = async (uploadLink) => {
    const fullUrl = `${window.location.origin}/upload/${uploadLink}`;

    // 降级方案函数
    const fallbackCopy = () => {
      const textArea = document.createElement('textarea');
      textArea.value = fullUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        message.success('上传链接已复制');
      } catch (copyError) {
        message.error('复制失败，请手动复制链接');
      }
      document.body.removeChild(textArea);
    };

    // 优先使用 Clipboard API
    if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(fullUrl);
        message.success('上传链接已复制');
      } catch (error) {
        fallbackCopy();
      }
    } else {
      fallbackCopy();
    }
  };

  const handleDeleteTourist = async (touristId) => {
    try {
      await axios.delete(`${API_BASE}/tourists/${touristId}`, {
        headers: authService.getAuthHeaders()
      });
      message.success('游客删除成功');
      // 重新获取游客列表
      fetchTourists(selectedTour.id);
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 导出证件照片打包下载。docScope: 'all'(护照+身份证) | 'passport' | 'chinaDoc'
  const handleExportPhotos = async (docScope = 'passport') => {
    if (exportingScope) return; // 防连点：重复触发会被浏览器拦截，且用户看不到任何错误
    const scopeLabel = docScope === 'chinaDoc' ? '身份证/台胞证' : '护照';
    setExportingScope(docScope);
    try {
      const touristsWithPhotos = tourists.filter(t => docScope === 'chinaDoc' ? t.chinaDocPhoto : t.passportPhoto);

      if (touristsWithPhotos.length === 0) {
        message.warning(`暂无${scopeLabel}照片可导出`);
        return;
      }

      message.loading(`正在打包${scopeLabel}照片...`, 2);

      const response = await axios.post(`${API_BASE}/export/passport-photos`, {
        tourId: selectedTour.id,
        touristIds: touristsWithPhotos.map(t => t.id),
        docScope
      }, {
        headers: authService.getAuthHeaders(),
        responseType: 'blob'
      });

      // 创建下载链接
      const blob = new Blob([response.data], { type: 'application/zip' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const fileLabel = docScope === 'chinaDoc' ? '身份证照片' : '护照照片';
      const fileName = `${selectedTour.productName}_${fileLabel}_${moment().format('YYYYMMDD')}.zip`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      // 浏览器不会回调下载结果，这里无从判断文件是否真的落盘，
      // 因此只报「已开始下载」并给出文件名（文件名用团号，与列表里的产品名不同，
      // 不告知的话用户会在下载目录里按产品名搜、然后以为没导出成功）。
      message.success(`已开始下载：${fileName}`, 6);
    } catch (error) {
      message.error('导出失败：' + (error.response?.data?.error || error.message));
    } finally {
      setExportingScope(null);
    }
  };

  // CSV导出功能
  const handleExportCSV = () => {
    const isDaytour = selectedTour?.tourType === 'daytour';
    // 排除已取消/退款，其余全部导出（出境游未验证的也导出，上传状态列可区分）
    const exportTourists = tourists.filter(tourist => tourist.orderStatus !== 'cancelled' && tourist.orderStatus !== 'refunded');

    if (exportTourists.length === 0) {
      message.warning('暂无游客记录可导出');
      return;
    }

    let csvHeaders, csvContent;

    if (isDaytour) {
      // ⚠️ 表头与下方 fields 必须逐位对应，任何一边增删都要同步改，否则整张表往后错一列
      csvHeaders = ['游客姓名', 'EKOK', '类型', '来源', '销售姓名', '联系电话', '联系邮箱', '上车点', '订单号', '证件号码', '证件类型', '出生日期', '出生地', '备注', '订单状态', '退款金额', '上传状态', '添加时间'];
      csvContent = [
        csvHeaders.join(','),
        ...exportTourists.map(tourist => {
          const orderStatusMap = { pending: '待确认', confirmed: '已确认', cancelled: '已取消', refunded: '已退款' };
          const fields = [
            tourist.touristName || '',
            tourist.ekok || '',
            tourist.discountPrice ? '优惠' : '原价',
            // 表头加了「来源」，这里必须同步加，否则整张表往后错一列
            ({ app: 'App', miniprogram: '小程序', web: '网页', admin: '后台' })[tourist.orderSource] || '',
            tourist.salesName || '',
          ].map(f => `"${f}"`);
          fields.push(fmtPhone(tourist.phone));
          fields.push(...[
            tourist.contactEmail || '',
            tourist.pickupPoint || '',
            tourist.orderNumber || '',
            tourist.documentNumber || '',
            tourist.documentType === 'passport' ? '护照' : (tourist.documentType === 'nz_driver_license' ? '驾照' : (tourist.documentType === 'nz_senior_card' ? 'SuperGold' : '')),
            tourist.birthDate ? moment(tourist.birthDate).format('YYYY-MM-DD') : '',
            // 表头「出生日期」之后是「出生地」，位置必须与 csvHeaders 一致
            tourist.birthPlace || '',
            tourist.remarks || '',
            orderStatusMap[tourist.orderStatus] || '',
            tourist.refundAmount != null ? String(tourist.refundAmount) : '',
            tourist.uploadStatus === 'verified' ? '已验证' : (tourist.uploadStatus === 'uploaded' ? '已上传' : (tourist.uploadStatus === 'pending' ? '待上传' : '已拒绝')),
            tourist.createdAt ? moment(tourist.createdAt).format('YYYY-MM-DD HH:mm') : ''
          ].map(f => `"${f}"`));
          return fields.join(',');
        })
      ].join('\n');
    } else {
      // 基础表头。
      // ⚠️ 第一列必须是护照姓名：这份导出是出票依据，取第一列必须永远拿到护照上的名字。
      // TTOUR 订单名是销售手打的、常有错拼/带 MR MS/用英文小名（线上实测 29 例，
      // 如 CHAN/BOO EE 实际护照是 CHAN/BOON TEE），单独放一列供对账、提醒回 TTOUR 改正。
      const baseHeaders = [
        '护照姓名', '规格', 'EKOK', '订单号', '类型', '销售姓名', '护照号码', 'TTOUR订单名',
        '性别', '国籍', '出生地', '主要联系人', '联系电话', '联系邮箱', '房型'
      ];
      const addonHeaders = availableAddOns.map(addon => addon.name);
      const tailHeaders = ['备注', '出生日期', '签发日期', '护照有效期', '机票号', '结算状态', '退款金额', '上传状态', '添加时间'];
      csvHeaders = [...baseHeaders, ...addonHeaders, ...tailHeaders];

      csvContent = [
        csvHeaders.join(','),
        ...exportTourists.map(tourist => {
          const dayTrips = tourist.dayTrips || {};
          const specLabel = [
            { with_tour: 'Plus款', no_tour: '基础款' }[tourist.packageType],
            { '2star': '二钻', '3star': '三钻', '4star': '四钻', '5star': '五钻' }[tourist.hotelTier],
          ].filter(Boolean).join('/') || '默认规格';
          const baseData = [
            // 护照名优先；还没上传护照的（passportName 为空）退回订单名，至少有个名字
            tourist.passportName || tourist.touristName || '',
            specLabel,
            tourist.ekok || '',
            tourist.ttourOrderSerial || tourist.orderNumber || '',
            tourist.touristType || '',
            tourist.salesName || '',
            tourist.passportNumber || '',
            tourist.touristName || '',
            tourist.gender === 'M' ? '男' : (tourist.gender === 'F' ? '女' : ''),
            tourist.nationality || '',
            tourist.birthPlace || '',
            tourist.contactPerson || '',
          ].map(f => `"${f}"`);
          baseData.push(fmtPhone(tourist.contactPhone));
          baseData.push(...[
            tourist.contactEmail || '',
            tourist.roomType || ''
          ].map(f => `"${f}"`));
          const addonData = availableAddOns.map(addon => {
            const key = `addon_${addon.id}`;
            return `"${dayTrips[key] ? 'Yes' : 'No'}"`;
          });
          const tailData = [
            tourist.remarks || '',
            tourist.passportBirthDate ? moment(tourist.passportBirthDate).format('YYYY-MM-DD') : '',
            tourist.passportIssueDate ? moment(tourist.passportIssueDate).format('YYYY-MM-DD') : '',
            tourist.passportExpiryDate ? moment(tourist.passportExpiryDate).format('YYYY-MM-DD') : '',
            tourist.ticketNumber || '',
            tourist.billingSettled ? '已结算' : '未结算',
            tourist.refundAmount != null ? String(tourist.refundAmount) : '',
            tourist.uploadStatus === 'verified' ? '已验证' : (tourist.uploadStatus === 'uploaded' ? '已上传' : (tourist.uploadStatus === 'pending' ? '待上传' : '已拒绝')),
            tourist.createdAt ? moment(tourist.createdAt).format('YYYY-MM-DD HH:mm') : ''
          ].map(f => `"${f}"`);
          return [...baseData, ...addonData, ...tailData].join(',');
        })
      ].join('\n');
    }

    // 创建下载
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${selectedTour.productName}_游客_${moment().format('YYYYMMDD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    reportExport({ tourId: selectedTour.id, kind: 'tour_csv', tourists: exportTourists, page: 'admin-tours' });

    message.success(`已导出 ${exportTourists.length} 条游客记录`);
  };

  // 格式化电话号码：补前导0，用="..."防止Excel去掉前导0
  const fmtPhone = (p) => {
    if (!p) return '""';
    const num = p.startsWith('0') ? p : '0' + p;
    return `="${num}"`;
  };

  // 导游CSV导出（一日游专用：按上车点→销售分组）
  const handleExportGuidePDF = async () => {
    // 排除已取消和退款
    const exportTourists = tourists.filter(t => t.orderStatus !== 'cancelled' && t.orderStatus !== 'refunded');
    if (exportTourists.length === 0) {
      message.warning('暂无可导出的游客记录');
      return;
    }

    // 排序：上车点 → 销售 → 客人姓名
    const pickupPoints = selectedTour?.destination?.pickupPoints;
    const pointOrder = Array.isArray(pickupPoints)
      ? pickupPoints.map(p => typeof p === 'string' ? p : p.name)
      : [];

    const sorted = [...exportTourists].sort((a, b) => {
      const pickupA = a.pickupPoint || '';
      const pickupB = b.pickupPoint || '';
      if (pickupA !== pickupB) {
        const ia = pointOrder.indexOf(pickupA);
        const ib = pointOrder.indexOf(pickupB);
        if (ia !== -1 && ib !== -1) return ia - ib;
        if (ia !== -1) return -1;
        if (ib !== -1) return 1;
        return pickupA.localeCompare(pickupB, 'zh');
      }
      const salesA = a.salesName || '';
      const salesB = b.salesName || '';
      if (salesA !== salesB) return salesA.localeCompare(salesB, 'zh');
      return (a.touristName || '').localeCompare(b.touristName || '', 'zh');
    });

    // 统计各上车点人数
    const pickupCounts = {};
    sorted.forEach(t => {
      const key = t.pickupPoint || '未选择';
      pickupCounts[key] = (pickupCounts[key] || 0) + 1;
    });

    const depDate = selectedTour.departureDate ? moment(selectedTour.departureDate).format('YYYY-MM-DD') : '';

    // 构建隐藏的 HTML 表格用于渲染 PDF
    const container = document.createElement('div');
    container.style.cssText = 'position:fixed;left:-9999px;top:0;width:800px;background:#fff;padding:30px;font-family:"Microsoft YaHei","PingFang SC",sans-serif;';

    const pickupSummary = Object.entries(pickupCounts).map(([p, c]) => `${p}: ${c}人`).join('　');

    container.innerHTML = `
      <h2 style="text-align:center;margin:0 0 4px;font-size:18px;">${selectedTour.productName}</h2>
      <p style="text-align:center;margin:0 0 12px;font-size:13px;color:#666;">${depDate}　　总人数: ${sorted.length}</p>
      <p style="text-align:center;margin:0 0 16px;font-size:12px;color:#888;">${pickupSummary}</p>
      <table style="width:100%;border-collapse:collapse;font-size:13px;">
        <thead>
          <tr style="background:#1890ff;color:#fff;">
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:center;width:40px;">序号</th>
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:left;">客人姓名</th>
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:left;">销售</th>
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:left;">上车点</th>
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:left;">联系电话</th>
            <th style="padding:8px 6px;border:1px solid #ccc;text-align:center;">票种</th>
          </tr>
        </thead>
        <tbody>
          ${sorted.map((t, i) => `
            <tr style="background:${i % 2 === 0 ? '#fff' : '#fafafa'};">
              <td style="padding:6px;border:1px solid #e8e8e8;text-align:center;">${i + 1}</td>
              <td style="padding:6px;border:1px solid #e8e8e8;">${t.touristName || '-'}</td>
              <td style="padding:6px;border:1px solid #e8e8e8;">${t.salesName || '-'}</td>
              <td style="padding:6px;border:1px solid #e8e8e8;">${t.pickupPoint || '-'}</td>
              <td style="padding:6px;border:1px solid #e8e8e8;">${t.phone || t.contactPhone || '-'}</td>
              <td style="padding:6px;border:1px solid #e8e8e8;text-align:center;">${t.discountPrice ? '优惠' : '正价'}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;

    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container, { scale: 1.5, useCORS: true });
      document.body.removeChild(container);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgRatio = pdfWidth / canvas.width;
      const scaledHeight = canvas.height * imgRatio;
      const pageHeight = pdfHeight - 10;
      const totalPages = Math.ceil(scaledHeight / pageHeight);

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage();
        const sourceY = (page * pageHeight) / imgRatio;
        const sourceH = Math.min(pageHeight / imgRatio, canvas.height - sourceY);
        const destH = sourceH * imgRatio;
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.ceil(sourceH);
        const ctx = pageCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceH, 0, 0, canvas.width, sourceH);
        pdf.addImage(pageCanvas.toDataURL('image/jpeg', 0.75), 'JPEG', 0, 5, pdfWidth, destH);
      }

      pdf.save(`${selectedTour.productName}_${depDate.replace(/-/g, '')}.pdf`);
      reportExport({ tourId: selectedTour.id, kind: 'guide_pdf', tourists: sorted, page: 'admin-tours' });
      message.success(`已导出 ${sorted.length} 条记录`);
    } catch (err) {
      document.body.removeChild(container);
      console.error('导出PDF失败:', err);
      message.error('导出PDF失败');
    }
  };

  // 锁定游客信息
  const handleLockTouristInfo = () => {
    setLockGuideId(selectedTour?.guide?.id || undefined);
    setLockModalVisible(true);
  };

  const handleConfirmLock = async () => {
    setLockLoading(true);
    try {
      // 如果选了导游，先分配导游
      if (lockGuideId) {
        await axios.put(`${API_BASE}/tours/${selectedTour.id}/guide`, { guideId: lockGuideId }, {
          headers: authService.getAuthHeaders()
        });
      }
      await axios.post(`${API_BASE}/tours/${selectedTour.id}/lock-tourist-info`, {}, {
        headers: authService.getAuthHeaders()
      });
      message.success('已截团，销售端将无法修改');
      setSelectedTour({
        ...selectedTour,
        touristInfoLocked: true,
        touristInfoLockedAt: new Date(),
        guideId: lockGuideId || selectedTour.guideId,
        guide: lockGuideId ? guides.find(g => g.id === lockGuideId) : selectedTour.guide
      });
      setLockModalVisible(false);
      fetchTours();
    } catch (error) {
      message.error(error.response?.data?.message || '截团失败');
    } finally {
      setLockLoading(false);
    }
  };

  // 解锁游客信息
  const handleUnlockTouristInfo = async () => {
    Modal.confirm({
      title: '解锁确认',
      icon: <ExclamationCircleOutlined />,
      content: '解锁后销售端将可以修改游客信息，确定要解锁吗？',
      okText: '确定解锁',
      cancelText: '取消',
      onOk: async () => {
        try {
          await axios.post(`${API_BASE}/tours/${selectedTour.id}/unlock-tourist-info`, {}, {
            headers: authService.getAuthHeaders()
          });
          message.success('游客信息已解锁，销售端可以修改');
          // 更新本地状态
          setSelectedTour({
            ...selectedTour,
            touristInfoLocked: false,
            touristInfoLockedAt: null
          });
          // 刷新产品列表
          fetchTours();
        } catch (error) {
          message.error(error.response?.data?.message || '解锁失败');
        }
      }
    });
  };

  // 批量发送出团通知
  const handleBatchSendNotice = () => {
    const isDaytour = selectedTour?.tourType === 'daytour';
    const eligible = isDaytour
      ? tourists.filter(t => t.orderStatus !== 'cancelled' && t.orderStatus !== 'refunded')
      : tourists;

    let description;
    if (isDaytour) {
      const groups = {};
      eligible.forEach(t => {
        const key = t.orderNumber || `_solo_${t.id}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(t);
      });
      const groupCount = Object.keys(groups).length;
      description = `共 ${eligible.length} 位游客，${groupCount} 个订单，将发送 ${groupCount} 封邮件（每个订单一封，附带所有游客的出团通知书PDF）`;
    } else {
      description = `确定要向 ${eligible.length} 位游客的销售发送出团通知吗？`;
    }

    Modal.confirm({
      title: '发送出团通知',
      icon: <NotificationOutlined />,
      content: description,
      okText: '确定发送',
      cancelText: '取消',
      onOk: () => {
        doSendNotice(selectedTour.id, eligible, () => fetchTourists(selectedTour.id));
      }
    });
  };

  // 逐组发送出团通知（共享逻辑）
  const doSendNotice = (tourId, eligibleTourists, onDone) => {
    setNoticeSending(true);
    setNoticeProgress(0);
    setNoticeProgressText('准备发送...');

    setTimeout(async () => {
      // 按 orderNumber 分组
      const groups = {};
      eligibleTourists.forEach(t => {
        const key = t.orderNumber || `_solo_${t.id}`;
        if (!groups[key]) groups[key] = [];
        groups[key].push(t);
      });
      const groupEntries = Object.entries(groups);
      const totalGroups = groupEntries.length;
      let sentCount = 0;
      let failedCount = 0;
      let skippedCount = 0;
      let firstError = '';
      let aborted = false;

      for (let i = 0; i < totalGroups; i++) {
        const [, members] = groupEntries[i];
        const names = members.map(t => t.touristName || t.passportName).join('、');
        const pct = Math.round(((i) / totalGroups) * 90) + 5;
        setNoticeProgress(pct);
        setNoticeProgressText(`正在发送 (${i + 1}/${totalGroups})：${names}`);

        try {
          const res = await axios.post(`${API_BASE}/tour-notices/tour/${tourId}/batch-send-emails`, {
            touristIds: members.map(t => t.id)
          }, {
            headers: authService.getAuthHeaders()
          });
          const data = res.data.data;
          sentCount += data.sentCount || 0;
          failedCount += data.failedCount || 0;
          skippedCount += data.skippedCount || 0;
        } catch (err) {
          failedCount++;
          if (!firstError) firstError = err.response?.data?.message || err.message;
          // 400 是整团层面的拒绝（如通知书未创建），后续订单结果相同，不再逐单请求
          if (err.response?.status === 400) {
            aborted = true;
            break;
          }
        }
      }

      setNoticeProgress(100);
      setNoticeProgressText('发送完成');
      setTimeout(() => {
        setNoticeSending(false);
        setNoticeProgress(0);
        if (aborted) {
          message.error(`发送失败：${firstError}`);
        } else {
          const summary = `发送完成：成功 ${sentCount} 封${failedCount > 0 ? `，失败 ${failedCount} 封` : ''}${skippedCount > 0 ? `，跳过 ${skippedCount} 人` : ''}`;
          if (failedCount > 0) message.warning(firstError ? `${summary}（${firstError}）` : summary);
          else message.success(summary);
        }
        if (onDone) onDone();
      }, 800);
    }, 300);
  };

  // 从列表发送出团通知（先加载游客数据）
  const handleSendNoticeFromList = async (record) => {
    try {
      const response = await axios.get(`${API_BASE}/tourists?tourId=${record.id}`, {
        headers: authService.getAuthHeaders()
      });
      const tourTourists = response.data.data;
      setTourists(tourTourists);
      setSelectedTour(record);

      const isDaytour = record.tourType === 'daytour';
      const eligible = isDaytour
        ? tourTourists.filter(t => t.orderStatus !== 'cancelled' && t.orderStatus !== 'refunded')
        : tourTourists;

      let description;
      if (isDaytour) {
        const groups = {};
        eligible.forEach(t => {
          const key = t.orderNumber || `_solo_${t.id}`;
          if (!groups[key]) groups[key] = [];
          groups[key].push(t);
        });
        const groupCount = Object.keys(groups).length;
        description = `共 ${eligible.length} 位游客，${groupCount} 个订单，将发送 ${groupCount} 封邮件（每个订单一封，附带所有游客的出团通知书PDF）`;
      } else {
        description = `确定要向 ${eligible.length} 位游客的销售发送出团通知吗？`;
      }

      Modal.confirm({
        title: '发送出团通知',
        icon: <NotificationOutlined />,
        content: description,
        okText: '确定发送',
        cancelText: '取消',
        onOk: () => {
          doSendNotice(record.id, eligible);
        }
      });
    } catch (error) {
      message.error('获取游客数据失败');
    }
  };

  // 修改导游
  const handleChangeGuide = () => {
    setChangeGuideId(selectedTour?.guide?.id || undefined);
    setChangeGuideVisible(true);
  };

  const handleConfirmChangeGuide = async () => {
    try {
      await axios.put(`${API_BASE}/tours/${selectedTour.id}/guide`, { guideId: changeGuideId || null }, {
        headers: authService.getAuthHeaders()
      });
      const guide = changeGuideId ? guides.find(g => g.id === changeGuideId) : null;
      setSelectedTour({
        ...selectedTour,
        guideId: changeGuideId || null,
        guide: guide
      });
      setChangeGuideVisible(false);
      message.success(guide ? `已分配导游：${guide.name}` : '已取消导游分配');
      fetchTours();
    } catch (error) {
      message.error(error.response?.data?.message || '修改导游失败');
    }
  };

  // 打开Add-On设置弹窗
  const openAddonSettingsModal = async (tour) => {
    setAddonSettingsTour(tour);
    setAddonModalVisible(true);
    setAddonLoading(true);

    try {
      const response = await axios.get(`${API_BASE}/tours/${tour.id}/addons`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        setAddonData({
          allAddOns: response.data.data.allAddOns || [],
          disabledAddonIds: response.data.data.disabledAddonIds || [],
          destination: response.data.data.destination
        });
      }
    } catch (error) {
      message.error('获取Add-On配置失败');
    } finally {
      setAddonLoading(false);
    }
  };

  // 切换Add-On启用/禁用状态
  const toggleAddonDisabled = (addonId) => {
    setAddonData(prev => {
      const isCurrentlyDisabled = prev.disabledAddonIds.includes(addonId);
      const newDisabledIds = isCurrentlyDisabled
        ? prev.disabledAddonIds.filter(id => id !== addonId)
        : [...prev.disabledAddonIds, addonId];
      return { ...prev, disabledAddonIds: newDisabledIds };
    });
  };

  // 保存Add-On设置
  const saveAddonSettings = async () => {
    if (!addonSettingsTour) return;

    try {
      await axios.put(`${API_BASE}/tours/${addonSettingsTour.id}/addons/disabled`, {
        disabledAddonIds: addonData.disabledAddonIds
      }, {
        headers: authService.getAuthHeaders()
      });
      message.success('Add-On设置已保存');
      setAddonModalVisible(false);
      setAddonSettingsTour(null);
    } catch (error) {
      message.error('保存Add-On设置失败');
    }
  };

  // 编辑游客
  const handleEditTourist = (tourist) => {
    setEditingTourist(tourist);
    setEkokWarning('');
    setNewPassportPhoto(null);
    setUploadProgress(0);

    const isDaytour = selectedTour?.tourType === 'daytour';

    if (isDaytour) {
      editForm.setFieldsValue({
        touristName: tourist.touristName,
        ekok: tourist.ekok,
        phone: tourist.phone,
        discountPrice: tourist.discountPrice || false,
        documentNumber: tourist.documentNumber,
        birthDate: tourist.birthDate ? moment(tourist.birthDate).format('YYYY-MM-DD') : '',
        remarks: tourist.remarks || '',
        pickupPoint: tourist.pickupPoint || undefined,
      });
    } else {
      editForm.setFieldsValue({
        touristName: tourist.touristName,
        passportName: tourist.passportName,
        passportNumber: tourist.passportNumber,
        nationality: tourist.nationality,
        gender: tourist.gender || 'M',
        birthPlace: tourist.birthPlace || '',
        passportBirthDate: tourist.passportBirthDate ? moment(tourist.passportBirthDate).format('YYYY-MM-DD') : '',
        passportExpiryDate: tourist.passportExpiryDate ? moment(tourist.passportExpiryDate).format('YYYY-MM-DD') : '',
        passportIssueDate: tourist.passportIssueDate ? moment(tourist.passportIssueDate).format('YYYY-MM-DD') : '',
        remarks: tourist.remarks || '',
      });
    }
  };

  const handleEditRemarks = (tourist) => {
    setEditingRemarks(tourist);
    setRemarksModalVisible(true);
    remarksForm.setFieldsValue({
      remarks: tourist.remarks || ''
    });
  };

  const handleUpdateRemarks = async (values) => {
    try {
      await axios.put(`${API_BASE}/tourists/${editingRemarks.id}`, {
        remarks: values.remarks
      }, {
        headers: authService.getAuthHeaders()
      });
      
      message.success('备注更新成功');
      setRemarksModalVisible(false);
      setEditingRemarks(null);
      remarksForm.resetFields();
      
      // 刷新游客列表
      fetchTourists(selectedTour.id);
    } catch (error) {
      message.error(error.response?.data?.error || '备注更新失败');
    }
  };

  // 更新订单状态（管理端一日游）
  const handleChangeOrderStatus = async (touristId, newStatus) => {
    // 退款需要弹窗输入金额
    if (newStatus === 'refunded') {
      const tourist = tourists.find(t => t.id === touristId);
      const fullPrice = tourist?.discountPrice
        ? (selectedTour?.seniorPrice != null ? parseFloat(selectedTour.seniorPrice) : '')
        : (selectedTour?.adultPrice != null ? parseFloat(selectedTour.adultPrice) : '');
      setRefundTourist(tourist);
      setRefundAmount(tourist?.refundAmount != null && tourist?.refundAmount !== '' ? tourist.refundAmount : fullPrice);
      setRefundModalVisible(true);
      return;
    }
    try {
      await axios.put(`${API_BASE}/tourists/${touristId}`, {
        orderStatus: newStatus
      }, {
        headers: authService.getAuthHeaders()
      });
      message.success('订单状态更新成功');
      fetchTourists(selectedTour.id);
    } catch (error) {
      message.error('订单状态更新失败');
    }
  };

  const handleRefundSubmit = async () => {
    if (!refundTourist) return;
    try {
      await axios.put(`${API_BASE}/tourists/${refundTourist.id}`, {
        orderStatus: 'refunded',
        refundAmount: refundAmount || 0
      }, {
        headers: authService.getAuthHeaders()
      });
      message.success('退款操作成功');
      setRefundModalVisible(false);
      setRefundTourist(null);
      fetchTourists(selectedTour.id);
    } catch (error) {
      message.error('退款操作失败');
    }
  };

  const handleUpdateRefundAmount = async () => {
    if (!refundTourist) return;
    try {
      await axios.put(`${API_BASE}/tourists/${refundTourist.id}`, {
        refundAmount: refundAmount || 0
      }, {
        headers: authService.getAuthHeaders()
      });
      message.success('退款金额已更新');
      setRefundModalVisible(false);
      setRefundTourist(null);
      fetchTourists(selectedTour.id);
    } catch (error) {
      message.error('更新失败');
    }
  };

  // 更新单个Add-On
  const handleUpdateSingleAddOn = async (touristId, addOnKey, value) => {
    try {
      // 先获取当前游客的dayTrips数据
      const tourist = tourists.find(t => t.id === touristId);
      const currentDayTrips = tourist?.dayTrips || {};

      // 更新指定的Add-On
      const updatedDayTrips = {
        ...currentDayTrips,
        [addOnKey]: value
      };

      await axios.put(`${API_BASE}/tourists/${touristId}/day-trips`, {
        dayTrips: updatedDayTrips
      }, {
        headers: authService.getAuthHeaders()
      });

      message.success('Add-On更新成功');
      fetchTourists(selectedTour.id);
    } catch (error) {
      message.error('Add-On更新失败');
    }
  };

  // 更新游客信息
  const handleUpdateTourist = async (values) => {
    const nameValidation = validatePassportName(values.touristName);
    if (!nameValidation.valid) {
      message.error(nameValidation.error);
      return;
    }

    // 护照姓名验证（如果有填写）
    if (values.passportName) {
      const passportNameValidation = validatePassportName(values.passportName);
      if (!passportNameValidation.valid) {
        message.error('护照姓名格式错误：' + passportNameValidation.error);
        return;
      }
      values.passportName = passportNameValidation.formatted;
    }

    try {
      const isDaytour = selectedTour?.tourType === 'daytour';
      let updatePayload;

      if (isDaytour) {
        updatePayload = {
          touristName: nameValidation.formatted,
          ekok: values.ekok || null,
          phone: values.phone || null,
          discountPrice: values.discountPrice || false,
          documentNumber: values.documentNumber || null,
          remarks: values.remarks,
          tourId: selectedTour.id,
          // 上车点下拉框只在团配了可选上车点时渲染；没渲染时表单里没有这个值，
          // 照样写 null 会把会员系统同步来的上车点清空（2026-10-21 教堂湾 10-24 团两人即此）
          ...(selectedTour?.destination?.pickupPoints?.length > 0 ? { pickupPoint: values.pickupPoint || null } : {}),
        };
        if (values.birthDate && values.birthDate !== 'Invalid date') {
          updatePayload.birthDate = values.birthDate;
        }
      } else {
        updatePayload = {
          touristName: nameValidation.formatted,
          passportName: values.passportName,
          passportNumber: values.passportNumber,
          nationality: values.nationality,
          gender: values.gender,
          passportBirthDate: values.passportBirthDate,
          passportExpiryDate: values.passportExpiryDate,
          passportIssueDate: values.passportIssueDate,
          remarks: values.remarks,
          tourId: selectedTour.id
        };
        // 出生地：出票/签证材料要用，且只有这里能人工补录（销售端会因表单为空而清空，已另行修复）。
        // 同样只在有值时提交，避免误清空 OCR 已识别的值。
        if (values.birthPlace && String(values.birthPlace).trim()) {
          updatePayload.birthPlace = String(values.birthPlace).trim().toUpperCase();
        }
      }
      await axios.put(`${API_BASE}/tourists/${editingTourist.id}`, updatePayload, {
        headers: authService.getAuthHeaders()
      });
      
      message.success('游客信息更新成功');
      setEditingTourist(null);
      editForm.resetFields();
      fetchTourists(selectedTour.id);
    } catch (error) {
      message.error(error.response?.data?.error || '更新失败');
    }
  };

  // 打开机票上传弹窗
  const openTicketModal = (tourist) => {
    setTicketUploadingTourist(tourist);
    setTicketOcrResult(null);
    setTicketUploadProgress(0);
    setTicketProgressText('');
    setTicketModalVisible(true);
  };

  // 关闭机票上传弹窗
  const closeTicketModal = () => {
    setTicketModalVisible(false);
    setTicketUploadingTourist(null);
    setTicketOcrResult(null);
    setTicketUploadProgress(0);
    setTicketProgressText('');
  };

  // 机票上传处理
  const handleTicketUpload = async (file) => {
    setTicketUploading(true);
    setTicketUploadProgress(0);
    setTicketOcrResult(null);

    // 进度条模拟
    const progressSteps = [
      { percent: 20, text: '正在上传机票图片...' },
      { percent: 40, text: '图片质量检查中...' },
      { percent: 60, text: 'AI智能识别中...' },
      { percent: 80, text: '解析机票信息...' },
      { percent: 95, text: '完成识别处理...' }
    ];

    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      if (stepIndex < progressSteps.length) {
        setTicketUploadProgress(progressSteps[stepIndex].percent);
        setTicketProgressText(progressSteps[stepIndex].text);
        stepIndex++;
      } else {
        clearInterval(progressInterval);
      }
    }, 800);

    const formData = new FormData();
    formData.append('ticket', file);

    try {
      const response = await axios.post(
        `${API_BASE}/tickets/upload`,
        formData,
        {
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      clearInterval(progressInterval);
      setTicketUploadProgress(100);
      setTicketProgressText('识别完成！');

      if (response.data.success) {
        setTicketOcrResult(response.data.data);
        message.success('机票信息识别成功');
      } else {
        message.error(response.data.error || '机票识别失败');
      }
    } catch (error) {
      clearInterval(progressInterval);
      setTicketUploadProgress(0);
      setTicketProgressText('');
      message.error('机票上传失败：' + (error.response?.data?.error || error.message));
    } finally {
      setTicketUploading(false);
    }

    return false; // 阻止默认上传行为
  };

  // 保存机票号码
  const handleSaveTicketNumber = async () => {
    if (!ticketOcrResult || !ticketUploadingTourist) {
      message.error('请先上传并识别机票');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE}/tickets/save/${ticketUploadingTourist.id}`,
        {
          ticketNumber: ticketOcrResult.ticketNumber,
          certNo: ticketOcrResult.certNo
        },
        {
          headers: authService.getAuthHeaders()
        }
      );

      if (response.data.success) {
        message.success('机票号码保存成功');
        closeTicketModal();
        // 刷新游客列表
        fetchTourists(selectedTour.id);
      } else {
        message.error(response.data.error || '保存失败');
      }
    } catch (error) {
      message.error('保存失败：' + (error.response?.data?.error || error.message));
    }
  };

  // 检查护照号码是否匹配
  const checkPassportMismatch = (tourist) => {
    if (!tourist.ticketCertNo || !tourist.passportNumber) {
      return false;
    }
    const normalizedCertNo = tourist.ticketCertNo.toUpperCase().replace(/\s+/g, '');
    const normalizedPassportNumber = tourist.passportNumber.toUpperCase().replace(/\s+/g, '');
    return normalizedCertNo !== normalizedPassportNumber;
  };

  // 护照照片上传处理
  const handlePassportUpload = async (file) => {
    setUploadingPhoto(true);
    setUploadProgress(0);
    
    // 更真实的进度条模拟
    const progressSteps = [
      { percent: 20, text: '正在上传图片...' },
      { percent: 40, text: '图片质量检查中...' },
      { percent: 60, text: 'AI智能识别中...' },
      { percent: 80, text: '解析护照信息...' },
      { percent: 95, text: '完成识别处理...' }
    ];
    
    let stepIndex = 0;
    const progressInterval = setInterval(() => {
      if (stepIndex < progressSteps.length) {
        setUploadProgress(progressSteps[stepIndex].percent);
        setProgressText(progressSteps[stepIndex].text);
        stepIndex++;
      } else {
        clearInterval(progressInterval);
      }
    }, 800);
    
    const formData = new FormData();
    formData.append('passport', file);
    formData.append('touristId', editingTourist.id);

    try {
      const response = await axios.post(
        `${API_BASE}/tourists/${editingTourist.id}/update-passport`,
        formData,
        {
          headers: {
            ...authService.getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data.success) {
        // 完成进度条
        clearInterval(progressInterval);
        setUploadProgress(100);
        setProgressText('识别完成！');
        
        setNewPassportPhoto(response.data.passportPhoto);
        message.success('护照照片上传成功');
        
        // 如果有识别结果，更新表单
        if (response.data.recognizedData) {
          const data = response.data.recognizedData;
          // 确保CHI被转换为CHN
          let nationality = data.nationality;
          if (nationality === 'CHI') {
            nationality = 'CHN';
          }

          editForm.setFieldsValue({
            passportName: data.fullName || data.recognizedName,
            passportNumber: data.passportNumber,
            nationality: nationality,
            gender: data.gender,
            // ⚠ OCR 返回的是 DD/MM/YYYY，必须显式给 moment 格式数组。
            // 不给格式时 moment 回落到 new Date()，那是按美式 MM/DD/YYYY 解析：
            //   "19/11/1961" → Invalid date（19 不是月）→ 原生 date input 拒收 → 出生日期显示空白
            //   "03/08/2026" → 2026-03-08（3月8日）→ 日月颠倒，且没有任何报错
            passportBirthDate: parseOcrDate(data.birthDate),
            passportExpiryDate: parseOcrDate(data.expiryDate),
            passportIssueDate: parseOcrDate(data.issueDate)
          });

          // 检查护照有效期是否距离出发日期小于6个月
          const departureDate = selectedTour?.departureDate || editingTourist?.departureDate;
          if (data.expiryDate && departureDate) {
            const expiryMoment = moment(data.expiryDate, ['YYYY-MM-DD', 'DD/MM/YYYY', 'YYYY/MM/DD']);
            const departureMoment = moment(departureDate);

            if (expiryMoment.isValid() && departureMoment.isValid()) {
              const monthsDiff = expiryMoment.diff(departureMoment, 'months', true);
              if (monthsDiff < 6) {
                Modal.warning({
                  title: '护照有效期提醒',
                  content: (
                    <div>
                      <p>护照有效期（<strong>{expiryMoment.format('YYYY-MM-DD')}</strong>）距离出团日期（<strong>{departureMoment.format('YYYY-MM-DD')}</strong>）
                      仅剩 <strong style={{ color: '#ff4d4f' }}>{Math.max(0, monthsDiff).toFixed(1)} 个月</strong>，不足6个月。</p>
                      <p>建议客人及时更新护照，以免影响出行。</p>
                    </div>
                  ),
                });
              }
            }
          }

          message.info('护照信息已自动识别并填入表单，请确认后保存');
        }
      }
    } catch (error) {
      clearInterval(progressInterval);
      setUploadProgress(0);
      setProgressText('');
      const respData = error.response?.data;
      if (respData?.error === 'INCOMPLETE_PASSPORT' || respData?.error === 'MRZ_UNVERIFIED') {
        message.error(respData.message || '护照照片未拍全，请把护照信息页所有信息和底部两行机读码（MRZ）完整拍入、无裁切遮挡后重新上传', 8);
      } else {
        message.error('护照照片上传失败：' + (respData?.error || error.message));
      }
    } finally {
      setUploadingPhoto(false);
    }

    return false; // 阻止默认上传行为
  };

  // 截团/解锁 直接操作
  const handleToggleLockFromList = async (record) => {
    if (record.touristInfoLocked) {
      // 解锁：简单确认
      Modal.confirm({
        title: '解锁确认',
        icon: <ExclamationCircleOutlined />,
        content: '解锁后销售端将可以修改游客信息，确定要解锁吗？',
        okText: '确定解锁',
        cancelText: '取消',
        onOk: async () => {
          try {
            await axios.post(`${API_BASE}/tours/${record.id}/unlock-tourist-info`, {}, {
              headers: authService.getAuthHeaders()
            });
            message.success('已解锁，销售端可以修改');
            fetchTours();
          } catch (error) {
            message.error(error.response?.data?.message || '操作失败');
          }
        }
      });
    } else {
      // 截团：加载游客数据，打开截团弹窗（和游客管理弹窗内一致）
      try {
        const response = await axios.get(`${API_BASE}/tourists?tourId=${record.id}`, {
          headers: authService.getAuthHeaders()
        });
        setTourists(response.data.data);
        setSelectedTour(record);
        // 加载目的地信息
        if (record.destinationId && !record.destination) {
          try {
            const destResponse = await axios.get(`${API_BASE}/destinations/${record.destinationId}`, {
              headers: authService.getAuthHeaders()
            });
            setSelectedTour(prev => ({ ...prev, destination: destResponse.data.data }));
          } catch (e) {}
        }
        setLockGuideId(record.guide?.id || undefined);
        setLockModalVisible(true);
      } catch (error) {
        message.error('获取游客数据失败');
      }
    }
  };

  // 更新团状态
  const handleUpdateTourStatus = async (record, status) => {
    const statusLabels = { active: '可预订', closed: '截团', not_bookable: '不可预订', cancelled: '已取消' };
    try {
      await axios.put(`${API_BASE}/tours/${record.id}/status`, { status }, {
        headers: authService.getAuthHeaders()
      });
      message.success(`团状态已更新为：${statusLabels[status]}`);
      fetchTours();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  // 切换出团可见性
  const handleToggleVisibility = (record) => {
    const newVisible = !record.isVisible;
    Modal.confirm({
      title: newVisible ? '开放预订' : '关闭预订',
      icon: <ExclamationCircleOutlined />,
      content: newVisible
        ? '开放后销售端将可以看到该团并添加游客，确定要开放预订吗？'
        : '关闭后销售端将看不到该团，无法添加游客，确定要关闭预订吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await axios.put(`${API_BASE}/tours/${record.id}/visibility`, {}, {
            headers: authService.getAuthHeaders()
          });
          message.success(newVisible ? '已开放预订，销售端可见' : '已关闭预订，销售端不可见');
          fetchTours();
        } catch (error) {
          message.error(error.response?.data?.message || '操作失败');
        }
      }
    });
  };


  // 操作列渲染（出境游和一日游共用）
  const renderActionColumn = (_, record) => (
    <Space size="small">
      <Tooltip title="管理游客">
        <Button
          size="small"
          type="primary"
          icon={<TeamOutlined />}
          onClick={() => openTouristModal(record)}
        />
      </Tooltip>
      <Tooltip title="发送出团通知">
        <Button
          size="small"
          icon={<NotificationOutlined />}
          style={{ color: '#1890ff', borderColor: '#1890ff' }}
          onClick={() => handleSendNoticeFromList(record)}
        />
      </Tooltip>
      <Tooltip title="通知书工作台">
        <Button
          size="small"
          icon={<SolutionOutlined />}
          style={{ color: '#52c41a', borderColor: '#52c41a' }}
          onClick={() => navigate(`/admin/tours/${record.id}/notice-workbench`)}
        />
      </Tooltip>
      {record.tourType !== 'daytour' && (
        <Tooltip title="Add-On设置">
          <Button
            size="small"
            icon={<GiftOutlined />}
            onClick={() => openAddonSettingsModal(record)}
            disabled={!record.destinationId}
          />
        </Tooltip>
      )}
      <Tooltip title="编辑">
        <Button
          size="small"
          icon={<EditOutlined />}
          onClick={() => {
            setSelectedTour(record);
            const dayjsDate = dayjs(record.departureDate);
            // 价格：优先用 Tour 自身的值，否则 fallback 到产品（Destination）默认价格
            const dest = record.destinationId ? destinations.find(d => d.id === record.destinationId) : null;
            form.setFieldsValue({
              productName: record.productName,
              departureDate: dayjsDate.isValid() ? dayjsDate : null,
              destinationId: record.destinationId || undefined,
              tourType: record.tourType || 'standard',
              // 不填默认 38：一日游团现在由会员系统推送并在那边管座位，
              // 这里预填一个数字，运营一点保存就会凭空写出个座位上限（界面随后显示 1/38 甚至误判满员）
              totalSeats: record.totalSeats ?? undefined,
              adultPrice: record.adultPrice != null ? parseFloat(record.adultPrice) : (dest?.adultPrice ? parseFloat(dest.adultPrice) : undefined),
              seniorPrice: record.seniorPrice != null ? parseFloat(record.seniorPrice) : (dest?.seniorPrice ? parseFloat(dest.seniorPrice) : undefined),
              adultCommission: record.adultCommission != null ? parseFloat(record.adultCommission) : (dest?.adultCommission ? parseFloat(dest.adultCommission) : undefined),
              seniorCommission: record.seniorCommission != null ? parseFloat(record.seniorCommission) : (dest?.seniorCommission ? parseFloat(dest.seniorCommission) : undefined)
            });
            setModalVisible(true);
          }}
        />
      </Tooltip>
      <Popconfirm
        title="确定要删除这个旅游产品吗？"
        onConfirm={() => handleDeleteTour(record.id)}
        okText="确定"
        cancelText="取消"
      >
        <Tooltip title="删除">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Tooltip>
      </Popconfirm>
    </Space>
  );

  // 团状态列
  const statusColorMap = { active: '#52c41a', closed: '#faad14', not_bookable: '#ff4d4f', cancelled: '#ff4d4f' };
  const statusBgMap = { active: '#f6ffed', closed: '#fffbe6', not_bookable: '#fff2f0', cancelled: '#fff2f0' };
  const statusBorderMap = { active: '#b7eb8f', closed: '#ffe58f', not_bookable: '#ffccc7', cancelled: '#ffccc7' };
  const tourStatusColumn = {
    title: '团状态',
    dataIndex: 'status',
    key: 'tourStatus',
    width: '8%',
    render: (status, record) => {
      const current = status || 'active';
      return (
        <Select
          value={current}
          size="small"
          variant="borderless"
          style={{
            minWidth: 0,
            width: 'auto',
            maxWidth: 110,
            background: statusBgMap[current],
            borderRadius: 6,
            border: `1px solid ${statusBorderMap[current]}`,
          }}
          onChange={(value) => handleUpdateTourStatus(record, value)}
          optionLabelProp="label"
          popupMatchSelectWidth={120}
          options={[
            { value: 'active', label: <span style={{ color: '#52c41a', fontWeight: 600 }}>可预订</span> },
            { value: 'closed', label: <span style={{ color: '#faad14', fontWeight: 600 }}>截团</span> },
            { value: 'not_bookable', label: <span style={{ color: '#ff4d4f', fontWeight: 600 }}>不可预订</span> },
            { value: 'cancelled', label: <span style={{ color: '#ff4d4f', fontWeight: 600 }}>已取消</span> },
          ]}
        />
      );
    },
  };

  // 出境游列定义（原样）
  const standardTourColumns = [
    {
      title: '产品',
      dataIndex: ['destination', 'name'],
      key: 'destination',
      width: '10%',
      sorter: (a, b) => (a.destination?.name || '').localeCompare(b.destination?.name || ''),
      render: (name) => name ? <Tag color="purple">{name}</Tag> : <span style={{ color: '#999' }}>未设置</span>,
    },
    {
      title: '出团日期',
      dataIndex: 'departureDate',
      key: 'departureDate',
      width: '8%',
      sorter: (a, b) => new Date(a.departureDate) - new Date(b.departureDate),
      render: (date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: '团号',
      dataIndex: 'productName',
      key: 'productName',
      width: '17%',
      sorter: (a, b) => (a.productName || '').localeCompare(b.productName || ''),
      render: (name, record) => (
        <Space size={4} wrap>
          <span style={{ fontSize: 13 }}>{name}</span>
          {record.status === 'closed' && <Tag color="orange" icon={<LockOutlined />} style={{ margin: 0 }}>截团</Tag>}
          {record.status === 'not_bookable' && <Tag color="red" icon={<EyeInvisibleOutlined />} style={{ margin: 0 }}>不可预订</Tag>}
          {record.status === 'cancelled' && <Tag color="default" style={{ margin: 0 }}>已取消</Tag>}
        </Space>
      ),
    },
    {
      title: '游客统计',
      key: 'touristStats',
      width: '20%',
      render: (_, record) => {
        const total = record.totalTourists || 0;
        const verified = record.verifiedTourists || 0;
        const pending = record.pendingTourists || 0;
        const cancelled = record.cancelledTourists || 0;
        const refunded = record.refundedTourists || 0;
        // 已能判定要交证件（护照已传、国籍中国/台湾）但还没交的人数
        const chinaDocPending = record.chinaDocPendingTourists || 0;
        const pct = total > 0 ? Math.round((verified / total) * 100) : 0;
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: pct === 100 ? '#52c41a' : '#1890ff', borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#666', whiteSpace: 'nowrap' }}>
                {verified}/{total}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Tag color="success" style={{ margin: 0, fontSize: 12 }}>已确认 {verified}</Tag>
              {pending > 0 && <Tag color="warning" style={{ margin: 0, fontSize: 12 }}>护照未上传 {pending}</Tag>}
              {chinaDocPending > 0 && (
                <Tooltip title="护照已传、按国籍应交身份证（台湾护照为台胞证）但尚未提交的人数">
                  <Tag color="orange" style={{ margin: 0, fontSize: 12 }}>身份证未传 {chinaDocPending}</Tag>
                </Tooltip>
              )}
              {cancelled > 0 && <Tag color="error" style={{ margin: 0, fontSize: 12 }}>已取消 {cancelled}</Tag>}
              {refunded > 0 && <Tag color="purple" style={{ margin: 0, fontSize: 12 }}>退款 {refunded}</Tag>}
            </div>
          </div>
        );
      },
    },
    tourStatusColumn,
    {
      title: '操作',
      key: 'action',
      width: '12%',
      render: renderActionColumn,
    },
  ];

  // 一日游列定义（进度条+Tag样式）
  const daytourTourColumns = [
    {
      title: '产品',
      dataIndex: ['destination', 'name'],
      key: 'destination',
      width: '10%',
      ellipsis: true,
      sorter: (a, b) => (a.destination?.name || '').localeCompare(b.destination?.name || ''),
      render: (name) => name ? <Tag color="purple">{name}</Tag> : <span style={{ color: '#999' }}>未设置</span>,
    },
    {
      title: '出团日期',
      dataIndex: 'departureDate',
      key: 'departureDate',
      width: '8%',
      sorter: (a, b) => new Date(a.departureDate) - new Date(b.departureDate),
      render: (date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: '团号',
      dataIndex: 'productName',
      key: 'productName',
      width: '15%',
      sorter: (a, b) => (a.productName || '').localeCompare(b.productName || ''),
      render: (name, record) => (
        <Space size={4} wrap>
          <span style={{ fontSize: 13 }}>{name}</span>
          {record.status === 'closed' && <Tag color="orange" icon={<LockOutlined />} style={{ margin: 0 }}>截团</Tag>}
          {record.status === 'not_bookable' && <Tag color="red" icon={<EyeInvisibleOutlined />} style={{ margin: 0 }}>不可预订</Tag>}
          {record.status === 'cancelled' && <Tag color="default" style={{ margin: 0 }}>已取消</Tag>}
        </Space>
      ),
    },
    {
      title: '游客统计',
      key: 'touristStats',
      width: '23%',
      render: (_, record) => {
        const total = record.totalTourists || 0;
        const verified = record.verifiedTourists || 0;
        const pending = record.pendingTourists || 0;
        const cancelled = record.cancelledTourists || 0;
        const refunded = record.refundedTourists || 0;
        const buses = Array.isArray(record.buses) ? record.buses : [];
        // 一日游座位数以会员系统分车表为准（A/B/C 车合计），没有分车时才用团上的 totalSeats
        const capacity = buses.length ? buses.reduce((n, b) => n + (b.maxCapacity || 0), 0) : (record.totalSeats || 0);
        // 会员系统推来的一日游团不带座位数，此时不能显示「1/0」——直接显示本团总人数
        const hasCapacity = capacity > 0;
        const pct = hasCapacity ? Math.min(Math.round((total / capacity) * 100), 100) : (total > 0 ? 100 : 0);
        const isFull = hasCapacity && total >= capacity;
        return (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', background: isFull ? '#ff4d4f' : '#1890ff', borderRadius: 3, transition: 'width 0.3s' }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 500, color: isFull ? '#ff4d4f' : '#666', whiteSpace: 'nowrap' }}>
                {hasCapacity ? `${total}/${capacity}` : `${total} 人`}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Tag color="success" style={{ margin: 0, fontSize: 12 }}>已确认 {verified}</Tag>
              <Tag color="warning" style={{ margin: 0, fontSize: 12 }}>待确认 {pending}</Tag>
              {cancelled > 0 && <Tag color="error" style={{ margin: 0, fontSize: 12 }}>已取消 {cancelled}</Tag>}
              {refunded > 0 && <Tag color="purple" style={{ margin: 0, fontSize: 12 }}>退款 {refunded}</Tag>}
            </div>
            {buses.length > 1 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                {buses.map((b) => (
                  <Tag key={b.id} color="geekblue" style={{ margin: 0, fontSize: 12 }}>{b.code} 车 {b.booked}/{b.maxCapacity}</Tag>
                ))}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '导游',
      key: 'guide',
      width: '6%',
      render: (_, record) => {
        if (record.guide) {
          return (
            <Space size={4}>
              <Tag color="purple" icon={<UserOutlined />} style={{ margin: 0 }}>{record.guide.name}</Tag>
              <Tooltip title="修改导游">
                <Button type="link" size="small" icon={<EditOutlined />} style={{ padding: 0 }} onClick={() => {
                  setSelectedTour(record);
                  setChangeGuideId(record.guide?.id || undefined);
                  setChangeGuideVisible(true);
                }} />
              </Tooltip>
            </Space>
          );
        }
        return (
          <Button type="link" size="small" onClick={() => {
            setSelectedTour(record);
            setChangeGuideId(undefined);
            setChangeGuideVisible(true);
          }}>
            分配导游
          </Button>
        );
      },
    },
    tourStatusColumn,
    {
      title: '操作',
      key: 'action',
      width: '11%',
      render: renderActionColumn,
    },
  ];

  // 标准团游客弹窗默认显示的高频列，其余列收进展开行（方案A：减少横向滚动）
  // 订单号/EKOK/联系人/电话 在订单分组表头行里展示，不占列
  const STANDARD_DEFAULT_TOURIST_KEYS = ['touristName', 'spec', 'roomType', 'documentNumber', 'nationality', 'uploadStatus', 'chinaDoc', 'action'];

  const touristColumns = [
    {
      title: '游客姓名',
      dataIndex: 'touristName',
      key: 'touristName',
      width: 180,
      fixed: 'left',
      ellipsis: {
        showTitle: false,
      },
      render: (name, record) => (
        <Tooltip placement="topLeft" title={name}>
          <span style={{
            textTransform: 'uppercase',
            ...(record.orderStatus === 'cancelled' || record.orderStatus === 'refunded' ? { textDecoration: 'line-through', color: '#999' } : {}),
          }}>
            {name}
          </span>
          {record.orderStatus === 'cancelled' && <Tag color="error" style={{ marginLeft: 6 }}>已取消</Tag>}
          {record.orderStatus === 'refunded' && <Tag color="default" style={{ marginLeft: 6 }}>已退款</Tag>}
        </Tooltip>
      ),
    },
    // 一日游这一列就是车次（A/B/C 车），原来对一日游整列隐藏，分车后名单上看不出谁坐哪台
    ...(selectedTour?.tourType === 'daytour' ? [{
      title: '车次',
      key: 'bus',
      width: 80,
      render: (_, record) => (record.busCode
        ? <Tag color="geekblue" style={{ margin: 0 }}>{record.busCode} 车</Tag>
        : <span style={{ color: '#bbb', fontSize: 12 }}>-</span>),
    }] : []),
    ...(selectedTour?.tourType !== 'daytour' ? [{
      title: '规格',
      key: 'spec',
      width: 160,
      ellipsis: false,
      // 规格完全由订单推送时确定，只读不可改
      render: (_, record) => {
        // 一日游的规格就是车次：A 车 / B 车（先放一台车报名，满了再加下一台）
        if (record.busCode) {
          return <Tag color="geekblue" style={{ margin: 0 }}>{record.busCode} 车</Tag>;
        }
        const pkg = { with_tour: { t: 'Plus款', c: 'blue' }, no_tour: { t: '基础款', c: 'default' } }[record.packageType];
        const tier = { '2star': { t: '二钻', c: 'green' }, '3star': { t: '三钻', c: 'gold' }, '4star': { t: '四钻', c: 'volcano' }, '5star': { t: '五钻', c: 'red' } }[record.hotelTier];
        if (!pkg && !tier) return <span style={{ color: '#bbb', fontSize: 12 }}>默认规格</span>;
        return (
          <Space size={4} style={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
            {pkg && <Tag color={pkg.c} style={{ margin: 0 }}>{pkg.t}</Tag>}
            {tier && <Tag color={tier.c} style={{ margin: 0 }}>{tier.t}</Tag>}
          </Space>
        );
      },
    }] : []),
    {
      title: 'EKOK',
      dataIndex: 'ekok',
      key: 'ekok',
      width: 80,
      ellipsis: false,
      render: (text, record, index) => {
        // 检查是否与前一行的EKOK相同
        const prevRecord = index > 0 ? tourists[index - 1] : null;
        const isSameAsPrev = prevRecord && prevRecord.ekok === text;

        return (
          <span style={{
            fontWeight: !isSameAsPrev && text ? 'bold' : 'normal',
            color: text ? '#1890ff' : '#999',
            whiteSpace: 'nowrap'
          }}>
            {text || '-'}
          </span>
        );
      },
    },
    {
      title: '订单号',
      dataIndex: 'ttourOrderSerial',
      // key 保持 'orderNumber'：一日游分组视图(下方 filtered)按此 key 过滤掉本列，订单号改在分组头显示
      key: 'orderNumber',
      width: 130,
      ellipsis: false,
      // 出境/入境游用 TTOUR 订单号(ttourOrderSerial)，一日游手工单用 orderNumber
      render: (_, record) => {
        const v = record.ttourOrderSerial || record.orderNumber;
        return v ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> : '-';
      },
    },
    ...(selectedTour?.tourType === 'daytour' ? [{
      title: '订单状态',
      dataIndex: 'orderStatus',
      key: 'orderStatus',
      width: 100,
      render: (status, record) => {
        if (!status) return '-';
        const colorMap = { pending: '#faad14', confirmed: '#52c41a', cancelled: '#ff4d4f', refunded: '#722ed1' };
        const textMap = { pending: '待确认', confirmed: '已确认', cancelled: '已取消', refunded: '退款' };
        return (
          <>
            <Select
              value={status}
              size="small"
              variant="borderless"
              style={{ width: 90 }}
              onChange={(value) => handleChangeOrderStatus(record.id, value)}
              optionLabelProp="label"
              popupMatchSelectWidth={100}
              options={[
                { value: 'pending', label: <span style={{ color: '#faad14', fontWeight: 500 }}>待确认</span> },
                { value: 'confirmed', label: <span style={{ color: '#52c41a', fontWeight: 500 }}>已确认</span> },
                { value: 'cancelled', label: <span style={{ color: '#ff4d4f', fontWeight: 500 }}>已取消</span> },
                { value: 'refunded', label: <span style={{ color: '#722ed1', fontWeight: 500 }}>退款</span> },
              ]}
            />
            {status === 'refunded' && (
              <span
                style={{ fontSize: 11, color: record.billingSettled ? '#999' : '#722ed1', cursor: record.billingSettled ? 'default' : 'pointer', marginLeft: 2 }}
                onClick={() => {
                  if (!record.billingSettled) {
                    setRefundTourist(record);
                    setRefundAmount(record.refundAmount || '');
                    setRefundModalVisible(true);
                  }
                }}
              >
                {record.refundAmount ? `-$${record.refundAmount}` : '填金额'}
              </span>
            )}
          </>
        );
      },
    }] : []),
    {
      title: '类型',
      dataIndex: 'touristType',
      key: 'touristType',
      width: 60,
      ellipsis: false,
      render: (type, record) => {
        if (selectedTour?.tourType === 'daytour') {
          return record.discountPrice
            ? <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>优惠</span>
            : <span>原价</span>;
        }
        if (type === 'ADT') {
          return <Tag color="blue">ADT</Tag>;
        } else if (type === 'CHD') {
          return <Tag color="green">CHD</Tag>;
        }
        return '-';
      },
    },
    ...(selectedTour?.tourType === 'daytour' ? [{
      title: '票价',
      dataIndex: 'price',
      key: 'price',
      width: 80,
      render: (v) => (v != null ? `NZ$${Number(v)}` : '-'),
    }] : []),
    {
      // 下单渠道。只有会员系统推来的自助单才有值 ——
      // 门店/顾问代下的单和 TTOUR 同步过来的单都留空，不硬填「线下」，
      // 那会把「确实是线下下的」和「老数据没记录」混成一样
      title: '来源',
      dataIndex: 'orderSource',
      key: 'orderSource',
      width: 84,
      render: (v) => {
        const m = {
          app: { text: 'App', color: 'geekblue' },
          miniprogram: { text: '小程序', color: 'green' },
          web: { text: '网页', color: 'purple' },
          admin: { text: '后台', color: 'default' },
        }[v];
        return m ? <Tag color={m.color}>{m.text}</Tag> : <span style={{ color: '#bfbfbf' }}>-</span>;
      },
    },
    {
      title: '销售',
      dataIndex: 'salesName',
      key: 'salesName',
      width: 100,
      ellipsis: { showTitle: false },
      render: (text) => text ? <Tooltip placement="topLeft" title={text}>{text}</Tooltip> : '-',
    },
    ...(selectedTour?.tourType === 'daytour' && selectedTour?.destination?.pickupPoints?.length > 0 ? [{
      title: '上车点',
      dataIndex: 'pickupPoint',
      key: 'pickupPoint',
      width: 200,
      ellipsis: false,
      render: (text) => {
        if (!text) return <span style={{ color: '#999' }}>-</span>;
        const points = normalizePickupPoints(selectedTour.destination.pickupPoints);
        const found = points.find(p => p.name === text);
        const display = found && found.time ? `${text} (${found.time})` : text;
        return <Tag color="cyan"><CarOutlined /> {display}</Tag>;
      },
    }] : []),
    ...(selectedTour?.tourType !== 'daytour' ? [{
      title: '护照号码',
      dataIndex: 'passportNumber',
      key: 'documentNumber',
      width: 100,
      ellipsis: false,
      render: (text, record) => {
        const isMismatch = checkPassportMismatch(record);
        if (!text) return '-';
        return (
          <Tooltip
            title={isMismatch ? `机票证件号: ${record.ticketCertNo}，与护照号码不匹配` : (record.ticketNumber ? `机票号: ${record.ticketNumber}` : null)}
          >
            <span style={{
              color: isMismatch ? '#ff4d4f' : 'inherit',
              fontWeight: isMismatch ? 'bold' : 'normal'
            }}>
              {text}
              {isMismatch && <ExclamationCircleOutlined style={{ marginLeft: 4, color: '#ff4d4f' }} />}
            </span>
            {record.passportReuse && <div><PassportReuseTag reuse={record.passportReuse} /></div>}
          </Tooltip>
        );
      },
    },
    {
      title: '护照姓名',
      dataIndex: 'passportName',
      key: 'passportName',
      width: 130,
      ellipsis: {
        showTitle: false,
      },
      render: (text) => text ? (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ) : '-',
    }] : []),
    ...(selectedTour?.tourType !== 'daytour' ? [
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 45,
      ellipsis: false,
      render: (gender) => {
        if (!gender) return '-';
        return gender === 'M' ? '男' : '女';
      },
    },
    {
      title: '国籍',
      dataIndex: 'nationality',
      key: 'nationality',
      width: 50,
      ellipsis: false,
      render: (code) => code || '-',
    },
    {
      title: '出生地',
      dataIndex: 'birthPlace',
      key: 'birthPlace',
      width: 100,
      ellipsis: { showTitle: false },
      render: (text) => text ? <Tooltip placement="topLeft" title={text}>{text}</Tooltip> : '-',
    },
    ] : []),
    ...(selectedTour?.tourType !== 'daytour' ? [{
      title: '主要联系人',
      dataIndex: 'contactPerson',
      key: 'contactPerson',
      width: 130,
      ellipsis: { showTitle: false },
      render: (text) => text ? <Tooltip placement="topLeft" title={text}>{text}</Tooltip> : '-',
    }] : []),
    {
      title: '联系电话',
      dataIndex: selectedTour?.tourType === 'daytour' ? 'phone' : 'contactPhone',
      key: 'contactPhone',
      width: 130,
      ellipsis: { showTitle: false },
      render: (text) => text ? <Tooltip placement="topLeft" title={text}>{text}</Tooltip> : '-',
    },
    ...(selectedTour?.tourType !== 'daytour' ? [{
      title: '联系邮箱',
      dataIndex: 'contactEmail',
      key: 'contactEmail',
      width: 200,
      ellipsis: { showTitle: false },
      render: (text) => text ? <Tooltip placement="topLeft" title={text}>{text}</Tooltip> : '-',
    }] : []),
    ...(selectedTour?.tourType !== 'daytour' ? [
    {
      title: '房型',
      dataIndex: 'roomType',
      key: 'roomType',
      width: 220,
      // 房型由 TTOUR 订单同步，只读不可改；完整显示（含拼房订单号），过长自动换行
      render: (roomType) => roomType
        ? <span style={{ wordBreak: 'break-all' }}>{roomType}</span>
        : <span style={{ color: '#999' }}>-</span>,
    },
    ] : []),
    ...(selectedTour?.tourType !== 'daytour' ? [
    {
      title: 'Add-Ons产品',
      dataIndex: 'dayTrips',
      key: 'addOns',
      width: 250,
      ellipsis: false,
      render: (dayTrips) => {
        if (!dayTrips || availableAddOns.length === 0) return <span style={{ color: '#999' }}>无</span>;

        // 颜色循环列表
        const colors = ['blue', 'purple', 'magenta', 'green', 'cyan', 'orange', 'gold', 'lime'];

        // 使用动态Add-On列表
        const purchased = availableAddOns.filter(addOn => {
          const key = `addon_${addOn.id}`;
          return dayTrips[key] === true;
        });

        if (purchased.length === 0) {
          return <span style={{ color: '#999' }}>无</span>;
        }

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {purchased.map((addOn, index) => (
              <Tag key={addOn.id} color={colors[index % colors.length]} style={{ margin: 0 }}>
                {addOn.name}
              </Tag>
            ))}
          </div>
        );
      },
    },
    // 动态生成Add-On选择列
    ...availableAddOns.map((addon, index) => ({
      title: addon.name,
      dataIndex: 'dayTrips',
      key: `addon_${addon.id}`,
      width: Math.max(110, addon.name.length * 10 + 40),
      ellipsis: false,
      render: (dayTrips, record) => {
        const addonKey = `addon_${addon.id}`;
        const currentValue = dayTrips?.[addonKey] || false;
        return (
          <Select
            value={currentValue}
            style={{ width: '100%' }}
            onChange={(value) => handleUpdateSingleAddOn(record.id, addonKey, value)}
            size="small"
          >
            <Option value={false}>No</Option>
            <Option value={true}>Yes</Option>
          </Select>
        );
      },
    })),
    ] : []),
    {
      title: '备注',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 160,
      ellipsis: { showTitle: false },
      render: (text) => text
        ? <Tooltip placement="topLeft" title={text}><span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{text}</span></Tooltip>
        : '-',
    },
    ...(selectedTour?.tourType !== 'daytour' ? [
    {
      title: '出生日期',
      dataIndex: 'passportBirthDate',
      key: 'passportBirthDate',
      width: 95,
      ellipsis: false,
      render: (date) => {
        if (!date) return '-';
        return moment(date).format('DD/MM/YYYY');
      },
    },
    {
      title: '签发日期',
      dataIndex: 'passportIssueDate',
      key: 'passportIssueDate',
      width: 95,
      ellipsis: false,
      render: (date) => {
        if (!date) return '-';
        return moment(date).format('DD/MM/YYYY');
      },
    },
    {
      title: '有效期',
      dataIndex: 'passportExpiryDate',
      key: 'passportExpiryDate',
      width: 95,
      ellipsis: false,
      render: (date) => {
        if (!date) return '-';
        return moment(date).format('DD/MM/YYYY');
      },
    },
    {
      title: '状态',
      dataIndex: 'uploadStatus',
      key: 'uploadStatus',
      width: 75,
      ellipsis: false,
      render: (status) => {
        const statusMap = {
          'pending': { text: '待上传', color: 'default' },
          'uploaded': { text: '已上传', color: 'processing' },
          'verified': { text: '已验证', color: 'success' },
          'rejected': { text: '已拒绝', color: 'error' }
        };
        const config = statusMap[status] || { text: status, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      // 中国团附加证件：护照传完之后接着补传身份证/台胞证（与销售端同一个组件）
      title: '身份证/台胞证',
      key: 'chinaDoc',
      width: 140,
      ellipsis: false,
      render: (_, record) => (
        <ChinaDocAction record={record} onDone={() => fetchTourists(selectedTour?.id)} />
      ),
    },
    {
      title: '机票号',
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      width: 130,
      ellipsis: false,
      render: (text, record) => {
        if (text) {
          return (
            <Tooltip title={record.ticketCertNo ? `证件号: ${record.ticketCertNo}` : null}>
              <span style={{ color: '#52c41a' }}>{text}</span>
            </Tooltip>
          );
        }
        return <span style={{ color: '#999' }}>-</span>;
      },
    },
    ] : []),
    {
      title: '操作',
      key: 'action',
      width: 140,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          {(selectedTour?.tourType === 'daytour' || record.uploadStatus !== 'pending') && (
            <Tooltip title="编辑">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditTourist(record)}
              />
            </Tooltip>
          )}
          {selectedTour?.tourType !== 'daytour' && record.uploadStatus !== 'verified' && (
            <Tooltip title="发给客人">
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyUploadLink(record.uploadLink)}
              />
            </Tooltip>
          )}
          {/* 眼睛 = 看全部证件（护照 + 身份证/台胞证）；单看证件走「身份证/台胞证」列里的查看 */}
          {(selectedTour?.tourType !== 'daytour' || record.discountPrice) && (record.passportPhoto || record.documentPhoto || record.chinaDocPhoto) && (
            <Tooltip title={selectedTour?.tourType === 'daytour' ? '查看证件'
              : (record.chinaDocPhoto ? `查看护照 / ${getChinaDocLabel(record) || '附加证件'}` : '查看护照')}>
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => {
                  setPassportModalRecord(record);
                  setPassportModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          {selectedTour?.tourType === 'standard' &&
            (authService.getUserInfo()?.role === 'admin' || authService.getUserInfo()?.role === 'operations') && (
              <Tooltip title={record.withdrawalStatus ? '已自愿退团 · 点击重发 Voucher' : '自愿退团（航变补偿）'}>
                <Button
                  size="small"
                  icon={<RollbackOutlined />}
                  type={record.withdrawalStatus ? 'default' : 'dashed'}
                  danger={!record.withdrawalStatus}
                  onClick={() => {
                    setWithdrawingTourist(record);
                    setWithdrawModalVisible(true);
                  }}
                />
              </Tooltip>
          )}
          <Popconfirm
            title="确定要删除这个游客吗？"
            description="删除后将无法恢复"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
            onConfirm={() => handleDeleteTourist(record.id)}
            okText="确定"
            cancelText="取消"
            okType="danger"
          >
            <Tooltip title="删除">
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const searchEkok = async (value) => {
    if (!value || !value.trim()) {
      setEkokMatchTourIds(null);
      return;
    }
    setEkokSearching(true);
    try {
      const response = await axios.get(`${API_BASE}/tourists`, {
        headers: authService.getAuthHeaders()
      });
      if (response.data.success) {
        const lower = value.toLowerCase().trim();
        const matched = response.data.data.filter(t =>
          t.ekok?.toLowerCase().includes(lower)
        );
        setEkokMatchTourIds([...new Set(matched.map(t => t.tourId))]);
      }
    } catch {
      // ignore
    } finally {
      setEkokSearching(false);
    }
  };

  // 筛选旅游产品
  const filteredTours = tours.filter(tour => {
    // 过滤已过期团期
    if (!showExpired) {
      const today = moment().startOf('day');
      if (moment(tour.departureDate).isBefore(today)) return false;
    }

    // 按搜索文本过滤。搜索框是「产品名称/EKOK」二选一，两者必须取并集：
    // 以前产品名和 EKOK 是 AND 关系，搜团号时 EKOK 匹配不到会把 ekokMatchTourIds 置成 []，
    // 于是产品名明明命中的团也被一起过滤掉，整页显示「暂无数据」。
    const keyword = searchProductName.trim();
    const nameHit = tour.productName?.toLowerCase().includes(keyword.toLowerCase());
    const ekokHit = ekokMatchTourIds !== null && ekokMatchTourIds.includes(tour.id);
    const searchFilter = keyword === '' || nameHit || ekokHit;

    // 按出团日期过滤
    const dateFilter = !searchDepartureDate ||
      moment(tour.departureDate).format('YYYY-MM-DD') === searchDepartureDate.format('YYYY-MM-DD');

    // 按游客姓名过滤（如果有搜索结果，只显示包含匹配游客的产品）
    const touristFilter = searchTouristName === '' || allTourists.length === 0 ||
      allTourists.some(t => t.tourId === tour.id);

    return searchFilter && dateFilter && touristFilter;
  });

  const sortedTours = [...filteredTours].sort((a, b) => {
    const da = new Date(a.departureDate);
    const db = new Date(b.departureDate);
    return showExpired ? db - da : da - db;
  });
  const standardTours = sortedTours.filter(t => t.tourType === 'standard');
  const inboundTours = sortedTours.filter(t => t.tourType === 'inbound');
  const daytourTours = sortedTours.filter(t => t.tourType === 'daytour');

  return (
    <div className="tour-management">
      <Card title="出团管理" className="management-card">
        {/* 搜索区域 */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col>
              <Input.Search
                placeholder="搜索产品名称/EKOK"
                value={searchProductName}
                onChange={(e) => { setSearchProductName(e.target.value); setCurrentPage(1); }}
                onSearch={(value) => { searchEkok(value); setCurrentPage(1); }}
                style={{ width: 220 }}
                allowClear
                enterButton={<SearchOutlined />}
                loading={ekokSearching}
                onClear={() => { setSearchProductName(''); setEkokMatchTourIds(null); setCurrentPage(1); }}
              />
            </Col>
            <Col>
              <Select
                placeholder="筛选出团日期"
                value={searchDepartureDate ? searchDepartureDate.format('YYYY-MM-DD') : undefined}
                onChange={(value) => { setSearchDepartureDate(value ? dayjs(value) : null); setCurrentPage(1); }}
                style={{ width: 160 }}
                allowClear
                showSearch
              >
                {/* 从tours中提取不重复的出团日期 */}
                {[...new Set(tours.map(t => t.departureDate ? moment(t.departureDate).format('YYYY-MM-DD') : null).filter(Boolean))]
                  .sort()
                  .map(date => (
                    <Option key={date} value={date}>{date}</Option>
                  ))}
              </Select>
            </Col>
            <Col>
              <Input.Search
                placeholder="搜索游客姓名/护照号/EKOK"
                value={searchTouristName}
                onChange={(e) => setSearchTouristName(e.target.value)}
                onSearch={(value) => { searchTouristsByName(value); setCurrentPage(1); }}
                style={{ width: 220 }}
                allowClear
                enterButton={<SearchOutlined />}
                loading={searchingTourist}
                prefix={<UserOutlined style={{ color: '#bfbfbf' }} />}
                onClear={() => {
                  setSearchTouristName('');
                  setAllTourists([]);
                  setCurrentPage(1);
                }}
              />
            </Col>
            <Col>
              <Checkbox checked={showExpired} onChange={(e) => setShowExpired(e.target.checked)}>往期开团</Checkbox>
            </Col>
            {allTourists.length > 0 && (
              <Col>
                <Tag color="blue">
                  找到 {allTourists.length} 位游客，涉及 {filteredTours.length} 个产品
                </Tag>
              </Col>
            )}
            <Col flex="auto">
              <Tooltip title="出团统一由 member 系统创建（出境/入境从 TTOUR 同步，一日游在 member 上架）。passport 不再手工建团。">
                <Button type="primary" icon={<PlusOutlined />} disabled>
                  新建团期（已停用·改由 member 建团）
                </Button>
              </Tooltip>
            </Col>
          </Row>
        </div>

        <Tabs activeKey={activeTab} onChange={(key) => { setActiveTab(key); sessionStorage.setItem('tourMgmt_activeTab', key); }} items={[
          {
            key: 'standard',
            label: `出境游 (${standardTours.length})`,
            children: (
              <Table
                columns={standardTourColumns}
                dataSource={standardTours}
                rowKey="id"
                loading={loading}
                scroll={{ x: 1200 }}
                size="small"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  total: standardTours.length,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                  pageSizeOptions: ['10', '20', '50', '100']
                }}
                onChange={handleTableChange}
              />
            ),
          },
          {
            key: 'inbound',
            label: `入境游 (${inboundTours.length})`,
            children: (
              <>
              <Table
                columns={standardTourColumns}
                dataSource={inboundTours}
                rowKey="id"
                loading={loading}
                scroll={{ x: 1200 }}
                size="small"
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                  pageSizeOptions: ['10', '20', '50', '100']
                }}
              />
              </>
            ),
          },
          {
            key: 'daytour',
            label: `一日游 (${daytourTours.length})`,
            children: (
              <Table
                columns={daytourTourColumns}
                dataSource={daytourTours}
                rowKey="id"
                loading={loading}
                scroll={{ x: 1200 }}
                size="small"
                pagination={{
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
                  pageSizeOptions: ['10', '20', '50', '100']
                }}
              />
            ),
          },
        ]} />
      </Card>

      <Modal
        title={selectedTour
          ? (selectedTour.tourType === 'daytour' ? '编辑一日游' : selectedTour.tourType === 'inbound' ? '编辑入境游' : '编辑出境游')
          : (activeTab === 'daytour' ? '新建一日游' : activeTab === 'inbound' ? '新建入境游' : '新建出境游')}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedTour(null);
          form.resetFields();
        }}
        footer={null}
        width={720}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={selectedTour ? handleUpdateTour : handleCreateTour}
          initialValues={{ tourType: 'standard', totalSeats: 38 }}
        >
          <Form.Item name="tourType" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.tourType !== currentValues.tourType}
          >
            {({ getFieldValue }) => {
              const tourType = getFieldValue('tourType');
              const filteredDests = destinations.filter(d => {
                if (tourType === 'daytour') return d.category === 'daytour';
                return d.category !== 'daytour';
              });
              return (
                <Form.Item
                  name="destinationId"
                  label="产品"
                  rules={[{ required: true, message: '请选择产品' }]}
                >
                  <Select
                    placeholder={tourType === 'daytour' ? '选择一日游产品' : '选择产品'}
                    showSearch
                    optionFilterProp="children"
                    onChange={(val) => {
                      if (tourType === 'daytour' && val) {
                        const dest = filteredDests.find(d => d.id === val);
                        if (dest) {
                          const updates = {};
                          if (dest.adultPrice) updates.adultPrice = parseFloat(dest.adultPrice);
                          if (dest.seniorPrice) updates.seniorPrice = parseFloat(dest.seniorPrice);
                          if (dest.adultCommission) updates.adultCommission = parseFloat(dest.adultCommission);
                          if (dest.seniorCommission) updates.seniorCommission = parseFloat(dest.seniorCommission);
                          if (Object.keys(updates).length > 0) form.setFieldsValue(updates);
                        }
                      }
                    }}
                  >
                    {filteredDests.map(d => (
                      <Option key={d.id} value={d.id}>{d.name}</Option>
                    ))}
                  </Select>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            name="departureDate"
            label="出团日期"
            rules={[{ required: true, message: '请选择出团日期' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
            />
          </Form.Item>

          {/* 编辑时显示团号（可改，但全库不能重复）*/}
          {selectedTour && (
            <Form.Item
              name="productName"
              label="团号"
              tooltip="可手动修改，但全库不能与其他团号重复"
              rules={[
                { required: true, message: '请输入团号' },
                {
                  validator: async (_, value) => {
                    if (!value || value.trim() === selectedTour.productName) return;
                    // 在已加载的 tours 列表里查重（轻量校验，提交时后端再做权威校验）
                    const dup = tours.find(t => t.id !== selectedTour.id && t.productName === value.trim());
                    if (dup) throw new Error(`团号已被「${dup.destination?.name || ''} #${dup.id}」占用`);
                  }
                }
              ]}
            >
              <Input placeholder="如 TCNNKGNKG8DP260508-1" />
            </Form.Item>
          )}

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.tourType !== currentValues.tourType}
          >
            {({ getFieldValue }) =>
              getFieldValue('tourType') === 'daytour' ? (
                <>
                  {/* 名额由会员系统的团期车次（A/B/C 车）决定并自动同步过来，
                      这边改了会被下一次同步覆盖，而且两边数字打架会让运营判断不了还能收几个人 */}
                  <Form.Item
                    name="totalSeats"
                    label="总座位数"
                    extra="由会员系统的团期名额自动同步，如需调整请在会员系统的「团期管理」里改车次名额"
                  >
                    <InputNumber
                      min={1}
                      max={9999}
                      style={{ width: '100%' }}
                      placeholder="由会员系统同步"
                      disabled
                    />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item
                        name="adultPrice"
                        label="正常价格"
                      >
                        <InputNumber
                          min={0}
                          step={0.01}
                          precision={2}
                          prefix="$"
                          style={{ width: '100%' }}
                          placeholder="正常价格"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        name="adultCommission"
                        label="原价提成"
                      >
                        <InputNumber
                          min={0}
                          step={0.01}
                          precision={2}
                          prefix="$"
                          style={{ width: '100%' }}
                          placeholder="原价提成"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        name="seniorPrice"
                        label="优惠价格（55岁+）"
                      >
                        <InputNumber
                          min={0}
                          step={0.01}
                          precision={2}
                          prefix="$"
                          style={{ width: '100%' }}
                          placeholder="优惠价格"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item
                        name="seniorCommission"
                        label="优惠价提成"
                      >
                        <InputNumber
                          min={0}
                          step={0.01}
                          precision={2}
                          prefix="$"
                          style={{ width: '100%' }}
                          placeholder="优惠价提成"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              ) : (
                <Form.Item
                  name="adultPrice"
                  label="团费 / 单人价（NZD）"
                  tooltip="该团的单人团费，用于自愿退团时计算退款 / Voucher 面值"
                >
                  <InputNumber
                    min={0}
                    step={0.01}
                    precision={2}
                    prefix="$"
                    style={{ width: '100%' }}
                    placeholder="1099.00"
                  />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedTour ? '更新' : '创建'}
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setSelectedTour(null);
                form.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontSize: 16, fontWeight: 'bold' }}>{selectedTour?.productName}</span>
              <Tag color="blue" style={{ marginLeft: 8 }}>
                共 {selectedTour?.tourType === 'daytour' ? tourists.filter(t => t.orderStatus !== 'cancelled' && t.orderStatus !== 'refunded').length : tourists.length} 人
              </Tag>
              {selectedTour?.departureDate && (
                <Tag color="green" style={{ marginLeft: 4 }}>
                  {moment(selectedTour.departureDate).format('YYYY-MM-DD')} 出发
                </Tag>
              )}
              {selectedTour?.touristInfoLocked && (
                <Tag color="red" icon={<LockOutlined />} style={{ marginLeft: 4 }}>
                  已截团
                </Tag>
              )}
              {selectedTour?.guide && (
                <>
                  <Tag color="purple" icon={<UserOutlined />} style={{ marginLeft: 4 }}>
                    导游：{selectedTour.guide.name}
                  </Tag>
                  <Tooltip title="修改导游">
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={handleChangeGuide} style={{ padding: 0, marginLeft: -4 }} />
                  </Tooltip>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginRight: 24 }}>
              {selectedTour?.touristInfoLocked ? (
                <Button
                  icon={<UnlockOutlined />}
                  onClick={handleUnlockTouristInfo}
                >
                  解锁修改
                </Button>
              ) : (
                <Button
                  icon={<LockOutlined />}
                  onClick={handleLockTouristInfo}
                >
                  截团
                </Button>
              )}
              {selectedTour?.tourType !== 'daytour' && (
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => handleExportPhotos('passport')}
                  loading={exportingScope === 'passport'}
                  disabled={!!exportingScope || tourists.filter(t => t.passportPhoto).length === 0}
                >
                  导出护照 ({tourists.filter(t => t.passportPhoto).length})
                </Button>
              )}
              {/* 中国团附加证件：本团有人传过身份证/台胞证才出现这个按钮，其它团不占位置 */}
              {selectedTour?.tourType !== 'daytour' && tourists.some(t => t.chinaDocPhoto) && (
                <Button
                  icon={<DownloadOutlined />}
                  onClick={() => handleExportPhotos('chinaDoc')}
                  loading={exportingScope === 'chinaDoc'}
                  disabled={!!exportingScope}
                >
                  导出身份证 ({tourists.filter(t => t.chinaDocPhoto).length})
                </Button>
              )}
              <Button
                icon={<FileTextOutlined />}
                onClick={handleExportCSV}
                disabled={tourists.filter(t => t.orderStatus !== 'cancelled' && t.orderStatus !== 'refunded').length === 0}
              >
                CSV导出 ({tourists.filter(t => t.orderStatus !== 'cancelled' && t.orderStatus !== 'refunded').length})
              </Button>
              {selectedTour?.tourType === 'daytour' && (
                <Button
                  icon={<FileTextOutlined />}
                  onClick={handleExportGuidePDF}
                  disabled={tourists.filter(t => t.orderStatus !== 'cancelled' && t.orderStatus !== 'refunded').length === 0}
                >
                  导出PDF ({tourists.filter(t => t.orderStatus !== 'cancelled' && t.orderStatus !== 'refunded').length})
                </Button>
              )}
            </div>
          </div>
        }
        open={touristModalVisible}
        onCancel={() => {
          setTouristModalVisible(false);
          setSelectedTour(null);
          setTourists([]);
        }}
        width={1400}
        footer={null}
      >
        <Table
          className="tour-tourist-table"
          columns={(() => {
            const isDaytour = selectedTour?.tourType === 'daytour';
            // 标准团：只显示高频列（其余在展开行），一日游：去掉组头里已有的列；两者都用订单分组表头
            const filtered = isDaytour
              ? touristColumns.filter(c => c.key !== 'orderNumber' && c.key !== 'contactPhone' && c.key !== 'ekok')
              : touristColumns.filter(c => STANDARD_DEFAULT_TOURIST_KEYS.includes(c.key));
            return filtered.map((col, idx) => ({
              ...col,
              fixed: undefined,
              onCell: (record, rowIndex) => {
                if (record._isGroupHeader) {
                  return idx === 0
                    ? { colSpan: filtered.length, style: { background: '#fafafa', padding: '6px 16px', borderBottom: '1px solid #f0f0f0' } }
                    : { colSpan: 0 };
                }
                const base = typeof col.onCell === 'function' ? col.onCell(record, rowIndex) : {};
                // 操作列点击不触发整行展开
                if (col.key === 'action') return { ...base, onClick: (e) => e.stopPropagation() };
                return base;
              },
              render: (...args) => {
                const record = args[1];
                if (record._isGroupHeader) {
                  if (idx === 0) {
                    return (
                      <div style={{ fontSize: 13 }}>
                        <span style={{ color: '#888' }}>订单号:</span>{' '}
                        <b style={{ fontFamily: 'monospace', color: '#262626' }}>{record.orderNumber}</b>
                        {record.ekok && (
                          <>
                            <span style={{ margin: '0 10px', color: '#d9d9d9' }}>|</span>
                            <span style={{ color: '#888' }}>EKOK:</span>{' '}
                            <b style={{ color: '#1890ff' }}>{record.ekok}</b>
                          </>
                        )}
                        <span style={{ margin: '0 10px', color: '#d9d9d9' }}>|</span>
                        <span style={{ color: '#888' }}>联系人:</span>{' '}
                        <b style={{ color: '#262626', textTransform: 'uppercase' }}>{record._mainContactName}</b>
                        <span style={{ margin: '0 10px', color: '#d9d9d9' }}>|</span>
                        <span style={{ color: '#888' }}>电话:</span>{' '}
                        <b style={{ color: '#262626' }}>{record._mainContactPhone || '-'}</b>
                        <span style={{ margin: '0 10px', color: '#d9d9d9' }}>|</span>
                        <span style={{ color: '#888' }}>销售:</span>{' '}
                        <b style={{ color: '#262626' }}>{record._salesNames || '-'}</b>
                        <span style={{ margin: '0 10px', color: '#d9d9d9' }}>|</span>
                        <span style={{ color: '#888' }}>{record._groupSize}人</span>
                        {record._confirmedCount > 0 && <Tag color="success" style={{ marginLeft: 6 }}>已确认 {record._confirmedCount}</Tag>}
                        {record._pendingCount > 0 && <Tag color="warning" style={{ marginLeft: 4 }}>待确认 {record._pendingCount}</Tag>}
                        {record._cancelledCount > 0 && <Tag color="error" style={{ marginLeft: 4 }}>已取消 {record._cancelledCount}</Tag>}
                        {record._refundedCount > 0 && <Tag color="purple" style={{ marginLeft: 4 }}>退款 {record._refundedCount}</Tag>}
                      </div>
                    );
                  }
                  return null;
                }
                return typeof col.render === 'function' ? col.render(...args) : (args[0] ?? '-');
              },
            }));
          })()}
          dataSource={(() => {
            // 统一按订单分组显示（组头行：订单号/EKOK/联系人/电话/人数/状态）
            // 一日游按订单号分组；标准团按 EKOK 分组（同 EKOK 的多个同步订单合并为一组）
            const isDaytourGrouping = selectedTour?.tourType === 'daytour';
            // 标准团订单号来自 TTOUR 同步（ttourOrderSerial），一日游用小程序订单号（orderNumber）
            const serialOf = (t) => t.ttourOrderSerial || t.orderNumber || '';
            // 会员系统推来的一日游老数据只有 ttourOrderSerial，没有 orderNumber
            const keyOf = (t) => isDaytourGrouping
              ? (t.orderNumber || t.ttourOrderSerial || `_solo_${t.id}`)
              : (t.ekok || serialOf(t) || `_solo_${t.id}`);
            const sorted = [...tourists].sort((a, b) =>
              keyOf(a).localeCompare(keyOf(b)) || serialOf(a).localeCompare(serialOf(b)) || (a.id - b.id)
            );
            const groups = {};
            let groupIdx = 0;
            sorted.forEach((t) => {
              const key = keyOf(t);
              if (!groups[key]) {
                groups[key] = { count: 0, firstId: t.id, groupIndex: groupIdx++, members: [] };
              }
              groups[key].count++;
              groups[key].members.push(t);
            });
            const result = [];
            let posInGroup = 0;
            let lastKey = null;
            sorted.forEach((t) => {
              const key = keyOf(t);
              const g = groups[key];
              if (key !== lastKey) {
                posInGroup = 0;
                lastKey = key;
                const mainContact = g.members[0];
                const confirmed = g.members.filter(m => m.orderStatus === 'confirmed').length;
                const pending = g.members.filter(m => m.orderStatus === 'pending').length;
                const cancelled = g.members.filter(m => m.orderStatus === 'cancelled').length;
                const refunded = g.members.filter(m => m.orderStatus === 'refunded').length;
                // 组内可能合并了多个同步订单号，去重后一起显示
                const orderNumbers = [...new Set(g.members.map(m => (isDaytourGrouping ? (m.orderNumber || m.ttourOrderSerial) : serialOf(m))).filter(Boolean))];
                // 客人联系电话：组内去重，多个不同电话用 / 分隔（标准团在 contactPhone，一日游在 phone）
                const phones = [...new Set(g.members.map(m => m.contactPhone || m.phone).filter(Boolean))];
                // 销售：组内去重，多个用 / 分隔
                const salesNames = [...new Set(g.members.map(m => m.salesName).filter(Boolean))];
                result.push({
                  _isGroupHeader: true,
                  id: `header_${key}`,
                  orderNumber: orderNumbers.length ? orderNumbers.join('、') : '-',
                  ekok: mainContact.ekok || '',
                  _mainContactName: mainContact.touristName || '-',
                  _mainContactPhone: phones.join(' / '),
                  _salesNames: salesNames.join(' / '),
                  _groupSize: g.count,
                  _confirmedCount: confirmed,
                  _pendingCount: pending,
                  _cancelledCount: cancelled,
                  _refundedCount: refunded,
                  _groupIndex: g.groupIndex,
                });
              }
              t._isLastInGroup = posInGroup === g.count - 1;
              t._groupIndex = g.groupIndex;
              posInGroup++;
              result.push(t);
            });
            return result;
          })()}
          rowKey="id"
          scroll={{ x: selectedTour?.tourType === 'daytour' ? undefined : 900 }}
          pagination={false}
          expandable={selectedTour?.tourType !== 'daytour' ? {
            columnWidth: 32,
            expandRowByClick: true,
            rowExpandable: (record) => !record._isGroupHeader,
            // 展开行展示被收起的列，复用各列原有 render 逻辑（订单号/EKOK 组头已有，不重复）
            expandedRowRender: (record) => {
              const hiddenCols = touristColumns.filter(c =>
                !STANDARD_DEFAULT_TOURIST_KEYS.includes(c.key)
                && !['pickupPoint', 'orderNumber', 'ekok'].includes(c.key)
              );
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '8px 24px', padding: '4px 8px 4px 24px' }}>
                  {hiddenCols.map(c => (
                    <div key={c.key} style={{ display: 'flex', gap: 8, fontSize: 12.5, alignItems: 'baseline', minWidth: 0 }}>
                      <span style={{ color: '#999', flexShrink: 0 }}>{typeof c.title === 'string' ? c.title : c.key}</span>
                      <span style={{ minWidth: 0, ...(['passportName', 'contactPerson'].includes(c.key) ? { textTransform: 'uppercase' } : {}) }}>
                        {typeof c.render === 'function'
                          ? c.render(c.dataIndex ? record[c.dataIndex] : undefined, record, 0)
                          : ((c.dataIndex && record[c.dataIndex]) ?? '-')}
                      </span>
                    </div>
                  ))}
                </div>
              );
            },
          } : undefined}
          onRow={(record) => {
            if (record._isGroupHeader) {
              return { style: { height: '36px', background: '#fafafa', cursor: 'default' } };
            }
            return {
              style: {
                ...(record._isLastInGroup ? { borderBottom: '2px solid #e0e0e0' } : {}),
                ...(selectedTour?.tourType !== 'daytour' ? { cursor: 'pointer' } : {}),
              }
            };
          }}
        />
      </Modal>

      {/* 编辑游客弹窗 */}
      {editingTourist && (
        <Modal
          title="编辑游客信息"
          open={!!editingTourist}
          onCancel={() => {
            setEditingTourist(null);
            setUploadProgress(0);
            setProgressText('');
            setNewPassportPhoto(null);
            editForm.resetFields();
          }}
          footer={null}
          width={700}
        >
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleUpdateTourist}
          >
            {/* ========== 一日游编辑表单 ========== */}
            {selectedTour?.tourType === 'daytour' ? (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="touristName"
                      label="姓名"
                      rules={[
                        { required: true, message: '请输入姓名' },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const validation = validatePassportName(value);
                            if (validation.valid) return Promise.resolve();
                            return Promise.reject(new Error(validation.error));
                          }
                        }
                      ]}
                      tooltip={getNameFormatHint()}
                    >
                      <Input
                        placeholder="姓/名 (例: ZHANG/SAN)"
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          editForm.setFieldsValue({ touristName: value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="ekok" label="EKOK"
                      validateStatus={ekokWarning ? 'warning' : undefined}
                      help={ekokWarning || undefined}
                    >
                      <Input placeholder="EKOK编号" style={{ textTransform: 'uppercase' }}
                        onBlur={async (e) => {
                          const val = e.target.value?.trim();
                          if (!val || !selectedTour?.id) { setEkokWarning(''); return; }
                          try {
                            const resp = await axios.get(`${API_BASE}/tourists/check-ekok`, {
                              params: { tourId: selectedTour.id, ekok: val, touristId: editingTourist?.id },
                              headers: authService.getAuthHeaders()
                            });
                            if (resp.data.exists) {
                              setEkokWarning(`该EKOK已存在（${resp.data.touristName}）`);
                            } else {
                              setEkokWarning('');
                            }
                          } catch { setEkokWarning(''); }
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="phone" label="联系电话" normalize={v => v ? v.replace(/\s/g, '') : v}>
                      <Input placeholder="联系电话" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="discountPrice" valuePropName="checked">
                      <Checkbox style={{ marginTop: 30 }}>享受优惠价（55岁以上）</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>

                {selectedTour?.destination?.pickupPoints?.length > 0 && (
                  <Form.Item name="pickupPoint" label="上车点">
                    <Select placeholder="选择上车点" allowClear>
                      {normalizePickupPoints(selectedTour.destination.pickupPoints).map((p, i) => (
                        <Option key={i} value={p.name}>{p.name}{p.time ? ` (${p.time})` : ''}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                {editingTourist?.documentType && (
                  <>
                    <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f5ff', border: '1px solid #adc6ff', borderRadius: 6, fontSize: 13, color: '#1d39c4' }}>
                      证件类型：{editingTourist.documentType === 'passport' ? '护照' : editingTourist.documentType === 'nz_driver_license' ? '新西兰驾照' : 'SuperGold Card'}
                    </div>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="documentNumber" label="证件号码">
                          <Input placeholder="证件号码" style={{ textTransform: 'uppercase' }} />
                        </Form.Item>
                      </Col>
                      {editingTourist.documentType !== 'nz_senior_card' && (
                        <Col span={12}>
                          <Form.Item name="birthDate" label="出生日期">
                            <Input placeholder="YYYY-MM-DD" type="date" />
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                  </>
                )}

                <Form.Item name="remarks" label="备注">
                  <Input.TextArea placeholder="请输入备注信息" rows={3} maxLength={500} showCount />
                </Form.Item>

                {/* 证件照片 */}
                {editingTourist?.documentPhoto && (
                  <Form.Item label="证件照片">
                    <Image
                      src={
                        editingTourist.documentPhoto.startsWith('http')
                          ? editingTourist.documentPhoto
                          : `${API_BASE.replace('/api', '')}${editingTourist.documentPhoto}`
                      }
                      alt="证件照片"
                      width={200}
                      height={120}
                      style={{ objectFit: 'cover', border: '1px solid #d9d9d9', borderRadius: 4 }}
                    />
                  </Form.Item>
                )}
              </>
            ) : (
              <>
                {/* ========== 出境游编辑表单 ========== */}
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="touristName"
                      label="游客姓名"
                      rules={[
                        { required: true, message: '请输入游客姓名' },
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const validation = validatePassportName(value);
                            if (validation.valid) return Promise.resolve();
                            return Promise.reject(new Error(validation.error));
                          }
                        }
                      ]}
                      tooltip={getNameFormatHint()}
                    >
                      <Input
                        placeholder="姓/名 (例: ZHANG/SAN)"
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          editForm.setFieldsValue({ touristName: value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="passportName"
                      label="护照姓名"
                      rules={[
                        {
                          validator: (_, value) => {
                            if (!value) return Promise.resolve();
                            const validation = validatePassportName(value);
                            if (validation.valid) return Promise.resolve();
                            return Promise.reject(new Error(validation.error));
                          }
                        }
                      ]}
                    >
                      <Input
                        placeholder="护照上的姓名"
                        style={{ textTransform: 'uppercase' }}
                        onChange={(e) => {
                          const value = e.target.value.toUpperCase();
                          editForm.setFieldsValue({ passportName: value });
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="passportNumber" label="护照号码">
                      <Input placeholder="护照号码" style={{ textTransform: 'uppercase' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="nationality" label="国籍">
                      <Select
                        placeholder="选择国籍"
                        showSearch
                        optionFilterProp="label"
                        filterOption={(input, option) =>
                          option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {getAllCountries().map(country => (
                          <Option key={country.value} value={country.value} label={country.label}>
                            {country.value} - {country.label}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item name="gender" label="性别">
                      <Select placeholder="选择性别">
                        <Option value="M">男</Option>
                        <Option value="F">女</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="passportBirthDate" label="出生日期">
                      <Input placeholder="YYYY-MM-DD" type="date" />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="passportIssueDate" label="签发日期">
                      <Input placeholder="YYYY-MM-DD" type="date" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="birthPlace" label="出生地">
                      <Input placeholder="城市英文名，如 SHANGHAI" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="passportExpiryDate" label="护照有效期">
                      <Input placeholder="YYYY-MM-DD" type="date" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="remarks" label="备注">
                  <Input.TextArea placeholder="请输入备注信息" rows={3} maxLength={500} showCount />
                </Form.Item>

                <Form.Item label="护照照片">
                  <div>
                    {editingTourist?.passportPhoto && (
                      <div style={{ marginBottom: 12 }}>
                        <Image
                          src={
                            editingTourist.passportPhoto.startsWith('http')
                              ? editingTourist.passportPhoto
                              : `${API_BASE.replace('/api', '')}${editingTourist.passportPhoto}`
                          }
                          alt="当前护照照片"
                          width={200}
                          height={120}
                          style={{ objectFit: 'cover', border: '1px solid #d9d9d9', borderRadius: 4 }}
                        />
                        <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>当前护照照片</div>
                      </div>
                    )}
                    {newPassportPhoto && (
                      <div style={{ marginBottom: 12 }}>
                        <Image
                          src={
                            newPassportPhoto.startsWith('http')
                              ? newPassportPhoto
                              : `${API_BASE.replace('/api', '')}${newPassportPhoto}`
                          }
                          alt="新护照照片"
                          width={200}
                          height={120}
                          style={{ objectFit: 'cover', border: '2px solid #52c41a', borderRadius: 4 }}
                        />
                        <div style={{ fontSize: 12, color: '#52c41a', marginTop: 4 }}>新上传的护照照片</div>
                      </div>
                    )}
                    <Upload
                      name="passport"
                      showUploadList={false}
                      beforeUpload={handlePassportUpload}
                      accept="image/*"
                      disabled={uploadingPhoto}
                    >
                      <Button
                        icon={<UploadOutlined />}
                        loading={uploadingPhoto}
                        disabled={uploadingPhoto}
                      >
                        {uploadingPhoto ? '上传中...' : '上传新护照照片'}
                      </Button>
                    </Upload>
                    {uploadingPhoto && (
                      <div style={{ margin: '12px 0' }}>
                        <Progress
                          percent={Math.round(uploadProgress)}
                          status="active"
                          strokeColor={{ '0%': '#108ee9', '100%': '#52c41a' }}
                          size="small"
                        />
                        <div style={{ fontSize: 11, color: '#1890ff', marginTop: 4, textAlign: 'center' }}>
                          {progressText || 'AI智能识别中，请稍候...'}
                        </div>
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
                      上传新的护照照片将自动识别并更新表单信息
                    </div>
                  </div>
                </Form.Item>

                {/* 中国团附加证件：与护照照片同一块区域，护照传完接着补证件 */}
                {editingTourist?.chinaDocRequired && (
                  <Form.Item label="身份证 / 台胞证">
                    <ChinaDocAction
                      mode="block"
                      record={editingTourist}
                      // 只刷新列表，不关闭编辑弹窗 —— 否则销售填到一半的表单会丢
                      onDone={() => fetchTourists(selectedTour?.id)}
                    />
                  </Form.Item>
                )}
              </>
            )}
            
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<EditOutlined />}>
                  更新信息
                </Button>
                <Button onClick={() => {
                  setEditingTourist(null);
                  setUploadProgress(0);
                  editForm.resetFields();
                }}>
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* 备注编辑弹窗 */}
      <Modal
        title="编辑备注"
        open={remarksModalVisible}
        onCancel={() => {
          setRemarksModalVisible(false);
          setEditingRemarks(null);
          remarksForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={remarksForm}
          layout="vertical"
          onFinish={handleUpdateRemarks}
        >
          <Form.Item
            name="remarks"
            label="备注信息"
          >
            <Input.TextArea
              rows={4}
              placeholder="请输入备注信息"
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<EditOutlined />}>
                保存备注
              </Button>
              <Button onClick={() => {
                setRemarksModalVisible(false);
                setEditingRemarks(null);
                remarksForm.resetFields();
              }}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看证件/护照弹窗 */}
      <Modal
        title={`${selectedTour?.tourType === 'daytour' ? '证件' : '护照'}照片 - ${passportModalRecord?.touristName || ''}`}
        open={passportModalVisible}
        onCancel={() => { setPassportModalVisible(false); setPassportModalRecord(null); }}
        footer={null}
        width={800}
      >
        {passportModalRecord && (() => {
          const isDaytourModal = selectedTour?.tourType === 'daytour';
          const photoUrl = isDaytourModal
            ? (passportModalRecord.documentPhoto || passportModalRecord.passportPhoto)
            : passportModalRecord.passportPhoto;
          return (
            <Row gutter={16}>
              <Col span={14}>
                {photoUrl ? (
                  <Image src={photoUrl.startsWith('http') ? photoUrl : `${API_BASE.replace('/api', '')}${photoUrl}`} alt="证件照片" style={{ width: '100%' }} />
                ) : (
                  <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>暂无照片</div>
                )}
              </Col>
              <Col span={10}>
                <div style={{ padding: '8px 0' }}>
                  <div><strong>姓名：</strong>{passportModalRecord.touristName || '-'}</div>
                  <div><strong>{isDaytourModal ? '证件号码' : '护照号码'}：</strong>{(isDaytourModal ? passportModalRecord.documentNumber : passportModalRecord.passportNumber) || '-'}</div>
                  {isDaytourModal ? (
                    <>
                      <div><strong>出生日期：</strong>{passportModalRecord.birthDate ? moment(passportModalRecord.birthDate).format('YYYY-MM-DD') : '-'}</div>
                      <div><strong>联系电话：</strong>{passportModalRecord.phone || '-'}</div>
                    </>
                  ) : (
                    <>
                      <div><strong>国籍：</strong>{passportModalRecord.nationality || '-'}</div>
                      <div><strong>性别：</strong>{passportModalRecord.gender === 'M' ? '男' : passportModalRecord.gender === 'F' ? '女' : '-'}</div>
                      <div><strong>出生日期：</strong>{passportModalRecord.passportBirthDate ? moment(passportModalRecord.passportBirthDate).format('YYYY-MM-DD') : '-'}</div>
                      <div><strong>签发日期：</strong>{passportModalRecord.passportIssueDate ? moment(passportModalRecord.passportIssueDate).format('YYYY-MM-DD') : '-'}</div>
                      <div><strong>有效期至：</strong>{passportModalRecord.passportExpiryDate ? moment(passportModalRecord.passportExpiryDate).format('YYYY-MM-DD') : '-'}</div>
                    </>
                  )}
                </div>
              </Col>
              {/* 操作列的眼睛 = 该客人的全部证件：护照下面接着排身份证/台胞证，同样左图右信息。
                  （「身份证/台胞证」列里的「查看」则只看证件本身） */}
              {!isDaytourModal && passportModalRecord.chinaDocPhoto && (
                <>
                  <Col span={24}>
                    <Divider orientation="left" plain style={{ margin: '16px 0 8px' }}>
                      {getChinaDocLabel(passportModalRecord) || '附加证件'}
                    </Divider>
                  </Col>
                  <Col span={14}>
                    <Image
                      src={passportModalRecord.chinaDocPhoto.startsWith('http')
                        ? passportModalRecord.chinaDocPhoto
                        : `${API_BASE.replace('/api', '')}${passportModalRecord.chinaDocPhoto}`}
                      alt={getChinaDocLabel(passportModalRecord) || '附加证件'}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  <Col span={10}>
                    <div style={{ padding: '8px 0', lineHeight: 2 }}>
                      <div><strong>证件类型：</strong>{getChinaDocLabel(passportModalRecord) || '-'}</div>
                      <div><strong>证件姓名：</strong>{passportModalRecord.chinaDocName || '-'}</div>
                      <div><strong>证件号码：</strong>{passportModalRecord.chinaDocNumber || '-'}</div>
                      <div><strong>有效期至：</strong>{passportModalRecord.chinaDocExpiryDate ? moment(passportModalRecord.chinaDocExpiryDate).format('YYYY-MM-DD') : '-'}</div>
                    </div>
                  </Col>
                </>
              )}
            </Row>
          );
        })()}
      </Modal>

      {/* 机票上传弹窗 */}
      <Modal
        title={
          <span>
            <UploadOutlined style={{ marginRight: 8 }} />
            上传机票 - {ticketUploadingTourist?.touristName}
          </span>
        }
        open={ticketModalVisible}
        onCancel={closeTicketModal}
        footer={[
          <Button key="cancel" onClick={closeTicketModal}>
            取消
          </Button>,
          <Button
            key="save"
            type="primary"
            disabled={!ticketOcrResult?.ticketNumber}
            onClick={handleSaveTicketNumber}
          >
            保存机票号码
          </Button>
        ]}
        width={600}
      >
        {/* 游客信息展示 */}
        {ticketUploadingTourist && (
          <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
            <Descriptions size="small" column={2}>
              <Descriptions.Item label="游客姓名">{ticketUploadingTourist.touristName}</Descriptions.Item>
              <Descriptions.Item label="护照号码">
                <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                  {ticketUploadingTourist.passportNumber || '-'}
                </span>
              </Descriptions.Item>
              {ticketUploadingTourist.ticketNumber && (
                <Descriptions.Item label="当前机票号">
                  <Tag color="green">{ticketUploadingTourist.ticketNumber}</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}

        {/* 上传区域 */}
        <Dragger
          name="ticket"
          accept=".pdf,.jpg,.jpeg,.png,.webp,image/*,application/pdf"
          showUploadList={false}
          beforeUpload={handleTicketUpload}
          disabled={ticketUploading}
          style={{ padding: '20px 0' }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: '#1890ff', fontSize: 48 }} />
          </p>
          <p className="ant-upload-text">点击或拖拽机票文件到此处上传</p>
          <p className="ant-upload-hint">
            支持 PDF、JPG、JPEG、PNG 格式的机票文件
          </p>
        </Dragger>

        {/* 上传进度 */}
        {ticketUploading && (
          <div style={{ margin: '16px 0' }}>
            <Progress
              percent={Math.round(ticketUploadProgress)}
              status="active"
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#52c41a',
              }}
            />
            <div style={{ fontSize: 12, color: '#1890ff', marginTop: 4, textAlign: 'center' }}>
              {ticketProgressText || 'AI智能识别中，请稍候...'}
            </div>
          </div>
        )}

        {/* 识别结果 */}
        {ticketOcrResult && (
          <div style={{ marginTop: 16, padding: 16, border: '1px solid #52c41a', borderRadius: 4, background: '#f6ffed' }}>
            <div style={{ fontWeight: 'bold', marginBottom: 12, color: '#52c41a' }}>
              识别结果
            </div>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label="机票号码 (Ticket Number)">
                <span style={{ fontWeight: 'bold', color: '#1890ff', fontSize: 16 }}>
                  {ticketOcrResult.ticketNumber || '-'}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="证件号码 (Cert NO.)">
                {(() => {
                  const certNo = ticketOcrResult.certNo;
                  const passportNumber = ticketUploadingTourist?.passportNumber;
                  if (!certNo) return '-';

                  const isMismatch = passportNumber &&
                    certNo.toUpperCase().replace(/\s+/g, '') !== passportNumber.toUpperCase().replace(/\s+/g, '');

                  return (
                    <span style={{
                      fontWeight: 'bold',
                      color: isMismatch ? '#ff4d4f' : '#52c41a',
                      fontSize: 16
                    }}>
                      {certNo}
                      {isMismatch && (
                        <Tag color="error" style={{ marginLeft: 8 }}>
                          <ExclamationCircleOutlined /> 与护照号码不匹配
                        </Tag>
                      )}
                      {!isMismatch && passportNumber && (
                        <Tag color="success" style={{ marginLeft: 8 }}>
                          与护照号码匹配
                        </Tag>
                      )}
                    </span>
                  );
                })()}
              </Descriptions.Item>
              {ticketOcrResult.passengerName && (
                <Descriptions.Item label="乘客姓名">
                  {ticketOcrResult.passengerName}
                </Descriptions.Item>
              )}
              {ticketOcrResult.confidence && (
                <Descriptions.Item label="识别置信度">
                  <Tag color={ticketOcrResult.confidence === 'high' ? 'green' : (ticketOcrResult.confidence === 'medium' ? 'orange' : 'red')}>
                    {ticketOcrResult.confidence === 'high' ? '高' : (ticketOcrResult.confidence === 'medium' ? '中' : '低')}
                  </Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* Add-On设置弹窗 */}
      <Modal
        title={
          <span>
            <GiftOutlined style={{ marginRight: 8 }} />
            Add-On产品设置 - {addonSettingsTour?.productName}
          </span>
        }
        open={addonModalVisible}
        onCancel={() => {
          setAddonModalVisible(false);
          setAddonSettingsTour(null);
          setAddonData({ allAddOns: [], disabledAddonIds: [] });
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setAddonModalVisible(false);
            setAddonSettingsTour(null);
          }}>
            取消
          </Button>,
          <Button key="save" type="primary" onClick={saveAddonSettings}>
            保存设置
          </Button>
        ]}
        width={600}
      >
        {addonLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin tip="加载中..." />
          </div>
        ) : (
          <div>
            {addonData.destination ? (
              <Alert
                message={`目的地: ${addonData.destination.name}`}
                description="以下是该目的地关联的Add-On产品，勾选表示启用，取消勾选表示禁用。"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            ) : (
              <Alert
                message="该产品未设置目的地"
                description="请先在产品编辑中选择目的地，才能配置Add-On产品。"
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {addonData.allAddOns.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>
                该目的地暂无Add-On产品
              </div>
            ) : (
              <div style={{ maxHeight: 400, overflow: 'auto' }}>
                {addonData.allAddOns.map(addon => {
                  const isDisabled = addonData.disabledAddonIds.includes(addon.id);
                  return (
                    <div
                      key={addon.id}
                      style={{
                        padding: '12px 16px',
                        marginBottom: 8,
                        border: '1px solid #d9d9d9',
                        borderRadius: 4,
                        background: isDisabled ? '#f5f5f5' : '#fff',
                        opacity: isDisabled ? 0.6 : 1
                      }}
                    >
                      <Checkbox
                        checked={!isDisabled}
                        onChange={() => toggleAddonDisabled(addon.id)}
                        style={{ width: '100%' }}
                      >
                        <div style={{ display: 'inline-block', marginLeft: 8 }}>
                          <div style={{ fontWeight: 500 }}>
                            {addon.name}
                            <Tag color="blue" style={{ marginLeft: 8 }}>第{addon.dayNumber}天</Tag>
                          </div>
                          {addon.summary && (
                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                              {addon.summary}
                            </div>
                          )}
                        </div>
                      </Checkbox>
                    </div>
                  );
                })}
              </div>
            )}

            {addonData.disabledAddonIds.length > 0 && (
              <div style={{ marginTop: 16, padding: 12, background: '#fff7e6', borderRadius: 4 }}>
                <span style={{ color: '#fa8c16' }}>
                  已禁用 {addonData.disabledAddonIds.length} 个Add-On产品
                </span>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 截团确认弹窗 */}
      <Modal
        title="截团确认"
        open={lockModalVisible}
        onCancel={() => setLockModalVisible(false)}
        width={480}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button onClick={() => setLockModalVisible(false)}>取消</Button>
            <Button type="primary" danger loading={lockLoading} onClick={handleConfirmLock}>确定截团</Button>
          </div>
        }
      >
        {(() => {
          const isDaytour = selectedTour?.tourType === 'daytour';
          const totalCount = tourists.length;
          const confirmedCount = tourists.filter(t => isDaytour ? t.orderStatus === 'confirmed' : t.uploadStatus === 'verified').length;
          const pendingCount = tourists.filter(t => isDaytour ? t.orderStatus === 'pending' : t.uploadStatus === 'pending').length;
          const cancelledCount = isDaytour ? tourists.filter(t => t.orderStatus === 'cancelled').length : 0;
          const refundedCount = isDaytour ? tourists.filter(t => t.orderStatus === 'refunded').length : 0;
          return (
            <div>
              <div style={{ background: '#fafafa', borderRadius: 6, padding: '12px 16px', marginBottom: 12 }}>
                <div style={{ fontWeight: 'bold', marginBottom: 8 }}>{selectedTour?.productName}</div>
                <div style={{ fontSize: 13, color: '#666', lineHeight: '22px' }}>
                  <div>团游类型：{isDaytour ? '一日游' : '出境游'}</div>
                  <div>出发日期：{selectedTour?.departureDate ? moment(selectedTour.departureDate).format('YYYY-MM-DD') : '-'}</div>
                  {selectedTour?.destination?.name && (
                    <div>产品：{selectedTour.destination.name}</div>
                  )}
                  <div>参团人数：{totalCount - cancelledCount - refundedCount} 人</div>
                  <div>
                    状态统计：已确认 {confirmedCount} 人
                    {pendingCount > 0 && <span style={{ color: '#faad14' }}>、证件未上传 {pendingCount} 人</span>}
                    {cancelledCount > 0 && <span style={{ color: '#ff4d4f' }}>、已取消 {cancelledCount} 人</span>}
                    {refundedCount > 0 && <span style={{ color: '#722ed1' }}>、退款 {refundedCount} 人</span>}
                  </div>
                  {isDaytour && selectedTour?.totalSeats && (
                    <div>座位使用：{totalCount - cancelledCount - refundedCount}/{selectedTour.totalSeats}</div>
                  )}
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ marginBottom: 6, fontSize: 13, color: '#666' }}>分配导游（可选）：</div>
                <Select
                  style={{ width: '100%' }}
                  placeholder="选择导游"
                  allowClear
                  value={lockGuideId}
                  onChange={setLockGuideId}
                >
                  {guides.map(g => (
                    <Option key={g.id} value={g.id}>{g.name}{g.phone ? ` (${g.phone})` : ''}</Option>
                  ))}
                </Select>
              </div>
              <div style={{ color: '#ff4d4f' }}>截团后销售端将无法进行修改、删除或添加游客，确定要截团吗？</div>
            </div>
          );
        })()}
      </Modal>

      {/* 修改导游弹窗 */}
      <Modal
        title="修改导游"
        open={changeGuideVisible}
        onCancel={() => setChangeGuideVisible(false)}
        onOk={handleConfirmChangeGuide}
        okText="确定"
        cancelText="取消"
        width={400}
      >
        <div style={{ marginBottom: 6, fontSize: 13, color: '#666' }}>选择导游：</div>
        <Select
          style={{ width: '100%' }}
          placeholder="选择导游"
          allowClear
          value={changeGuideId}
          onChange={setChangeGuideId}
        >
          {guides.map(g => (
            <Option key={g.id} value={g.id}>{g.name}{g.phone ? ` (${g.phone})` : ''}</Option>
          ))}
        </Select>
      </Modal>

      {/* 发送出团通知进度 */}
      <Modal
        title="发送出团通知"
        open={noticeSending}
        footer={null}
        closable={false}
        width={400}
        maskClosable={false}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <Progress
            percent={noticeProgress}
            status={noticeProgress >= 100 ? 'success' : 'active'}
            strokeColor={{
              '0%': '#1890ff',
              '100%': '#52c41a',
            }}
          />
          <div style={{ marginTop: 12, color: '#666', fontSize: 14 }}>{noticeProgressText}</div>
        </div>
      </Modal>

      {/* 退款金额弹窗 */}
      <Modal
        title={refundTourist?.orderStatus === 'refunded' ? '修改退款金额' : '退款'}
        open={refundModalVisible}
        onCancel={() => { setRefundModalVisible(false); setRefundTourist(null); }}
        footer={
          refundTourist?.billingSettled ? (
            <Button onClick={() => setRefundModalVisible(false)}>关闭</Button>
          ) : (
            <Space>
              <Button onClick={() => { setRefundModalVisible(false); setRefundTourist(null); }}>取消</Button>
              <Button type="primary" onClick={refundTourist?.orderStatus === 'refunded' ? handleUpdateRefundAmount : handleRefundSubmit}>
                {refundTourist?.orderStatus === 'refunded' ? '更新金额' : '确认退款'}
              </Button>
            </Space>
          )
        }
        width={400}
      >
        <div style={{ marginBottom: 12 }}>
          <strong>游客：</strong>{refundTourist?.touristName || refundTourist?.passportName || '-'}
        </div>
        {refundTourist?.billingSettled ? (
          <div>
            <div style={{ marginBottom: 8 }}>退款金额：<strong style={{ color: '#722ed1', fontSize: 18 }}>${refundTourist?.refundAmount || 0}</strong></div>
            <Alert message="该订单已结算，无法修改退款金额" type="warning" showIcon />
          </div>
        ) : (
          <div>
            <div style={{ marginBottom: 4 }}>退款金额：</div>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              step={0.01}
              precision={2}
              prefix="$"
              placeholder="请输入退款金额"
              value={refundAmount}
              onChange={setRefundAmount}
            />
          </div>
        )}
      </Modal>

      <VoucherWithdrawalModal
        open={withdrawModalVisible}
        tourist={withdrawingTourist}
        tour={selectedTour}
        onClose={() => { setWithdrawModalVisible(false); setWithdrawingTourist(null); }}
        onSuccess={() => {
          // 刷新游客列表，状态会变成 withdrawalStatus=true
          if (selectedTour?.id) {
            axios.get(`${API_BASE}/tourists/tour/${selectedTour.id}`, {
              headers: authService.getAuthHeaders()
            }).then(res => {
              if (res.data.success) setTourists(res.data.data);
            }).catch(() => {});
          }
        }}
      />
    </div>
  );
}

export default TourManagement;